import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MobileBottomTabBar, MobileTopHeader } from "../MobileAppShell";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import {
  getMystudioErrorMessage,
  useApplyPublicEventV4,
  useMystudioGalleryPostV4,
  useMystudioGalleryPostsV4,
  useMystudioHomeV4,
  useRegisterMystudioGalleryViewV4,
} from "@/Common/api/v4";
import {
  getSafeHttpsDownloadUrl,
  getSafeInternalAppPath,
} from "@/Common/api/v4/safeUrl";
import { APP_PAGE_KEYS, getSectionProps, getVisibleSections } from "@/Common/appConfig/appConfig";
import { useAppPageConfig } from "@/Common/hooks/useAppConfig";
import ActionCenter from "../ActionCenter";
import ConfigurableHomeSection from "../AppConfig/ConfigurableHomeSection";
import { ConfiguredPageIntro } from "../AppConfig/ConfiguredPageState";
import MystudioDataState from "../MystudioDataState";
import { getCurrentCustomerId, getCustomerScopedStorage } from "../customerScheduleStorage";
import { MOBILE_DUMMY_IMAGES as HOME_DUMMY_IMAGES } from "../mobileDummyImages";
import {
  CUSTOMER_ACTION_TYPES,
  CUSTOMER_ACTION_COUNT_EVENT,
  buildCustomerActionItems,
  cacheCustomerActionCount,
  filterCompletedCustomerActionItems,
  filterSnoozedActionItems,
  snoozeActionItem,
} from "./customerActionItems";
import {
  TODAY_TASK_FILTERS,
  filterTodayTaskItems,
  getTodayTaskCounts,
  getTodayTaskFilter,
  getUrgentTodayTask,
} from "./todayTaskDashboard";
import {
  EVENT_STATUS_TABS,
  filterEventsByStatus,
  normalizeEventBranch,
  normalizeEventStatus,
  rankStoreEvents,
  sortOngoingEvents,
} from "./eventHub";
import {
  formatPublicContentDate,
  mapPublicContentItems,
  resolvePublicContentSources,
} from "./publicContent";
import "./HomePage.css";
import MobileNotificationPage from "./MobileNotificationPage";
import { resolveHomeAvailability } from "./homeAvailability";
import { isPublicPasswordlessDemoSession } from "../../Common/api/v4/previewGuard";
import { getAccountData } from "../../Common/ts/commonTools";

const TODAY_TASK_LIST_TITLE_ID = "today-task-list-title";

const ASSETS = {
  bell: "/static/img/mobile-header/header-bell.svg",
  logoMaskA: "/static/img/mobile-header/header-logo-mask-a.svg",
  logoFillA: "/static/img/mobile-header/header-logo-fill-a.svg",
  logoMaskB: "/static/img/mobile-header/header-logo-mask-b.svg",
  logoFillB: "/static/img/mobile-header/header-logo-fill-b.svg",
  logoVectorA: "/static/img/mobile-header/header-logo-vector-a.svg",
  logoVectorB: "/static/img/mobile-header/header-logo-vector-b.svg",
  logoText: "/static/img/mobile-header/header-logo-text.svg",
  welcomeObject: "/static/img/home/home-hero-7133-1048-tablet.png",
  statTheme: "/static/img/home/home-stat-theme.svg",
  statPhoto: "/static/img/home/home-stat-photo.svg",
  downloadThumb: "/static/img/home/dummy/home-dummy-pure-window.jpg",
  schedulePhoto: "/static/img/home/dummy/home-dummy-family-garden.jpg",
  prepThumb: "/static/img/home/home-upcoming-prep-megaphone.png",
  galleryFamily: "/static/img/home/dummy/home-dummy-family-garden.jpg",
  galleryMaternity: "/static/img/home/dummy/home-dummy-maternity.jpg",
  galleryBaby: "/static/img/home/dummy/home-dummy-bunny-baby.jpg",
  galleryFigmaBaby: "/static/img/home/home-figma-7184-3652.png",
  galleryFigmaPure: "/static/img/home/home-figma-7187-3655.png",
  galleryChevron: "/static/img/home/home-gallery-7184-3551-chevron.svg",
  reviewEventMain: "/static/img/home/home-review-event-6396-27174-main.png",
  storeEventMain: "/static/img/home/home-store-event-6396-26981-main.png",
  storeEventCalendar: "/static/img/home/home-store-event-6396-26981-calendar.svg",
  galleryChevron6396: "/static/img/home/home-gallery-6396-27036-chevron.svg",
  themeChevron: "/static/img/home/home-theme-6396-27253-chevron.svg",
  noticeChat: "/static/img/home/home-notice-6396-27344-chat.svg",
  noticeHeroBg: "/static/img/home/home-notice-6396-27344-hero-bg.png",
  noticePostIcon: "/static/img/home/home-notice-6396-27344-notice-icon.png",
  noticeFaqIcon: "/static/img/home/home-notice-6396-27344-faq-icon.png",
  noticeGrayChevron: "/static/img/home/home-notice-6396-27344-gray-chevron.svg",
  recentChevron: "/static/img/home/home-chevron-right.svg",
  recentCalendar: "/static/img/home/home-recent-calendar.svg",
  recentTimeFace: "/static/img/home/home-recent-time-face.svg",
  recentTimeHand: "/static/img/home/home-recent-time-hand.svg",
  recentLocation: "/static/img/home/home-recent-location.svg",
  recentCamera: "/static/img/home/home-recent-camera.svg",
  recentDownload: "/static/img/home/home-recent-download.svg",
  upcomingPrepMegaphone: "/static/img/home/home-upcoming-prep-megaphone.png",
  prepGuideMegaphone: "/static/img/home/home-prep-6396-27374-megaphone.png",
  upcomingCalendar: "/static/img/home/home-upcoming-calendar.svg",
  upcomingTimeFace: "/static/img/home/home-upcoming-time-face.svg",
  upcomingTimeHand: "/static/img/home/home-upcoming-time-hand.svg",
  upcomingLocation: "/static/img/home/home-upcoming-location.svg",
  upcomingCamera: "/static/img/home/home-upcoming-camera.svg",
  upcomingChevronOrange: "/static/img/home/home-upcoming-chevron-orange.svg",
  upcomingChevronGray: "/static/img/home/home-upcoming-chevron-gray.svg",
  prepGuideChevron: "/static/img/home/home-prep-6396-27374-chevron.svg",
};

const FALLBACK_SCHEDULE = {
  badge: "테마선택 필요",
  rawDate: "2026-07-25",
  dDay: "D-23",
  packageName: "24/34 성장 STORY #A",
  title: "돌 / 100일 촬영",
  date: "2026.07.25(토)",
  time: "12:00 ~ 14:00 (30분 소요 예정)",
  theme: "화이트 가든",
  studio: "베이비파스텔 강남점",
  type: "추가촬영",
  thumbnail: HOME_DUMMY_IMAGES.familyGarden,
};

const FALLBACK_RECENT_SHOOTING = {
  badge: "촬영 완료",
  packageName: "24/34 성장 STORY #A",
  title: "돌 / 100일 촬영",
  date: "2026.06.25(목)",
  time: "12:00 ~ 14:00 (30분 소요 예정)",
  studio: "베이비파스텔 강남점",
  type: "추가촬영",
};

const FALLBACK_REVIEW_PHOTOS = [
  { title: "화이트 가든 가족 촬영", branch: "강남점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.familyGarden, category: "가족사진" },
  { title: "햇살 아래 첫돌 스냅", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.babyCrown, category: "가족사진" },
  { title: "핑크룸 생일 촬영", branch: "잠실점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.birthdayPink, category: "돌사진" },
  { title: "포근한 50일 베이비컷", branch: "일산점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.bunnyBaby, category: "50일" },
  { title: "만삭의 우아한 실루엣", branch: "마포점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.maternity, category: "만삭사진" },
  { title: "퓨어 윈도우 100일 촬영", branch: "송도점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "100일" },
];

const GALLERY_CATEGORY_FALLBACKS = {
  전체: FALLBACK_REVIEW_PHOTOS,
  가족사진: [
    { title: "홈 스냅 느낌의 자연스러운 연출", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.familyGarden, category: "가족사진" },
    { title: "화이트 가든 가족 촬영", branch: "강남점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.familyGarden, category: "가족사진" },
    { title: "햇살 아래 첫돌 스냅", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.babyCrown, category: "가족사진" },
    { title: "따뜻한 우리 가족의 오늘", branch: "일산점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "가족사진" },
    { title: "장난스러운 홈스타일 가족 컷", branch: "송도점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.playfulBaby, category: "가족사진" },
    { title: "꽃 정원 속 가족 이야기", branch: "마포점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.familyGarden, category: "가족사진" },
  ],
  만삭사진: [
    { title: "만삭의 우아한 실루엣", branch: "마포점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.maternity, category: "만삭사진" },
    { title: "화이트 드레스 만삭 촬영", branch: "강남점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.maternity, category: "만삭사진" },
    { title: "부드러운 흑백 만삭 컷", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.maternity, category: "만삭사진" },
    { title: "기다림을 담은 스튜디오 컷", branch: "일산점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.maternity, category: "만삭사진" },
    { title: "빛으로 완성한 만삭 사진", branch: "송도점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.maternity, category: "만삭사진" },
    { title: "차분한 감성의 엄마 기록", branch: "잠실점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.maternity, category: "만삭사진" },
  ],
  "50일": [
    { title: "포근한 50일 베이비컷", branch: "일산점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.bunnyBaby, category: "50일" },
    { title: "토끼 친구들과 함께한 순간", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.bunnyBaby, category: "50일" },
    { title: "말랑한 담요 속 50일", branch: "강남점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.bunnyBaby, category: "50일" },
    { title: "순한 눈빛의 베이비 스냅", branch: "마포점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.bunnyBaby, category: "50일" },
    { title: "아기 토끼 콘셉트 촬영", branch: "송도점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.bunnyBaby, category: "50일" },
    { title: "첫 미소를 기다리는 컷", branch: "잠실점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.bunnyBaby, category: "50일" },
  ],
  "100일": [
    { title: "퓨어 윈도우 100일 촬영", branch: "송도점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "100일" },
    { title: "창가 햇살의 100일 기록", branch: "마포점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "100일" },
    { title: "하얀 침구 위 자연광 컷", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "100일" },
    { title: "맑은 표정의 100일 사진", branch: "강남점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "100일" },
    { title: "퓨어 베이비 감성 촬영", branch: "일산점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "100일" },
    { title: "하얀 빛으로 담은 하루", branch: "잠실점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.pureWindow, category: "100일" },
  ],
  돌사진: [
    { title: "핑크룸 생일 촬영", branch: "잠실점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.birthdayPink, category: "돌사진" },
    { title: "생일 파티 돌 촬영", branch: "강남점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.birthdayPink, category: "돌사진" },
    { title: "왕관을 쓴 첫 생일", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.babyCrown, category: "돌사진" },
    { title: "핑크 소품 가득한 돌상 컷", branch: "일산점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.birthdayPink, category: "돌사진" },
    { title: "아기 공주의 생일 기록", branch: "송도점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.birthdayPink, category: "돌사진" },
    { title: "밝은 미소의 돌 촬영", branch: "마포점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.babyCrown, category: "돌사진" },
  ],
  기타: [
    { title: "장난스러운 홈스타일 컷", branch: "송도점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.playfulBaby, category: "기타" },
    { title: "선글라스 베이비 콘셉트", branch: "강남점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.playfulBaby, category: "기타" },
    { title: "감성 소품과 함께한 하루", branch: "분당점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.playfulBaby, category: "기타" },
    { title: "편안한 홈무드 촬영", branch: "마포점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.playfulBaby, category: "기타" },
    { title: "작은 장난감과 웃음", branch: "일산점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.playfulBaby, category: "기타" },
    { title: "자유로운 베이비 스냅", branch: "잠실점", date: "2026.06.06", image: HOME_DUMMY_IMAGES.playfulBaby, category: "기타" },
  ],
};

const STORE_EVENTS = [
  {
    id: "bundang-family-summer",
    title: "분당점 여름 가족촬영 이벤트",
    benefitTitle: "액자 업그레이드와 앨범 50% 할인",
    description: "가족 촬영 고객님께 액자 업그레이드와 앨범 추가 할인 혜택을 드려요.",
    branch: "MY STUDIO 분당점",
    branchName: "분당점",
    period: "2026.07.10(금) ~ 2026.07.15(수)",
    shortPeriod: "7월 15일까지",
    image: ASSETS.storeEventMain,
    category: "shooting",
    audience: "가족 촬영 고객 전용",
    status: "ending",
    statusLabel: "D-3",
    ctaLabel: "지금 신청하기",
    benefits: ["액자 업그레이드 혜택", "앨범 추가 50% 할인", "가족 촬영 고객 전용"],
    notice: "이벤트 혜택은 지점 일정과 계약 상품에 따라 달라질 수 있어요.",
  },
  {
    id: "gangnam-dol-album",
    title: "강남점 돌 촬영 앨범 업그레이드",
    benefitTitle: "프리미엄 앨범 페이지 무료 추가",
    description: "돌 촬영을 예약하면 프리미엄 앨범 페이지를 추가해드려요.",
    branch: "MY STUDIO 강남점",
    branchName: "강남점",
    period: "2026.07.10(금) ~ 2026.08.15(토)",
    shortPeriod: "8월 15일까지",
    image: HOME_DUMMY_IMAGES.birthdayPink,
    category: "product",
    audience: "돌 촬영 고객 전용",
    status: "open",
    statusLabel: "신청 가능",
    ctaLabel: "이벤트 신청하기",
    benefits: ["프리미엄 앨범 페이지 추가", "돌상 촬영 고객 전용", "선착순 예약 혜택"],
    notice: "예약 가능 시간과 제공 구성은 강남점 상담 후 확정됩니다.",
  },
  {
    id: "mapo-hundred-frame",
    title: "마포점 100일 촬영 액자 선물",
    benefitTitle: "테이블 액자 1개 선물",
    description: "100일 촬영 고객님께 테이블 액자 1개를 선물로 드려요.",
    branch: "MY STUDIO 마포점",
    branchName: "마포점",
    period: "2026.07.15(수) ~ 2026.08.31(월)",
    shortPeriod: "8월 31일까지",
    image: HOME_DUMMY_IMAGES.pureWindow,
    category: "product",
    audience: "100일 촬영 고객 전용",
    status: "open",
    statusLabel: "NEW",
    ctaLabel: "이벤트 자세히 보기",
    benefits: ["테이블 액자 1개 증정", "100일 촬영 고객 전용", "촬영 당일 수령 안내"],
    notice: "액자 디자인은 촬영 콘셉트와 지점 재고에 따라 달라질 수 있어요.",
  },
  {
    id: "all-review-frame",
    title: "촬영 후기 작성 액자 선물 이벤트",
    benefitTitle: "후기를 남기고 미니 액자 선물 받기",
    description: "촬영 후기를 남겨주신 고객님께 소중한 사진을 담은 미니 액자를 드려요.",
    branch: "전 지점",
    branchName: "전 지점",
    period: "2026.07.01(수) ~ 2026.07.22(수)",
    shortPeriod: "7월 22일까지",
    image: ASSETS.reviewEventMain,
    category: "review",
    audience: "촬영 완료 고객 전용",
    status: "ending",
    statusLabel: "D-10",
    ctaLabel: "후기 이벤트 신청하기",
    benefits: ["미니 액자 1개 증정", "촬영 후기 작성 고객 전용", "지점 확인 후 혜택 안내"],
    notice: "후기 확인과 액자 제작 일정은 신청 지점에서 별도로 안내드려요.",
  },
  {
    id: "gangnam-spring-retouch",
    title: "강남점 봄 가족사진 리터치 이벤트",
    benefitTitle: "가족사진 정밀 리터치 1컷 제공",
    description: "봄 가족 촬영 고객님께 정밀 리터치 사진 한 컷을 추가 제공한 이벤트예요.",
    branch: "MY STUDIO 강남점",
    branchName: "강남점",
    period: "2026.03.01(일) ~ 2026.05.31(일)",
    shortPeriod: "5월 31일 종료",
    image: HOME_DUMMY_IMAGES.familyGarden,
    category: "shooting",
    audience: "가족 촬영 고객 전용",
    status: "closed",
    statusLabel: "종료",
    ctaLabel: "이벤트 다시보기",
    benefits: ["정밀 리터치 1컷 제공", "가족 촬영 고객 전용", "참여 고객 개별 전달"],
    notice: "종료된 이벤트로 신규 신청은 할 수 없어요.",
  },
  {
    id: "all-album-upgrade-past",
    title: "앨범 페이지 무료 업그레이드 이벤트",
    benefitTitle: "앨범 페이지 무료 업그레이드",
    description: "촬영 고객님께 앨범 페이지를 추가 제공한 지난 이벤트예요.",
    branch: "전 지점",
    branchName: "전 지점",
    period: "2026.01.05(월) ~ 2026.03.31(화)",
    shortPeriod: "3월 31일 종료",
    image: HOME_DUMMY_IMAGES.babyCrown,
    category: "product",
    audience: "앨범 계약 고객 전용",
    status: "closed",
    statusLabel: "종료",
    ctaLabel: "이벤트 다시보기",
    benefits: ["앨범 페이지 무료 추가", "앨범 계약 고객 전용", "참여 고객 제작 반영"],
    notice: "종료된 이벤트로 신규 신청은 할 수 없어요.",
  },
];

const REVIEW_EVENT_PHOTO = {
  title: "후기 이벤트 사진",
  branch: "MY STUDIO",
  date: "2026.06.25",
  image: ASSETS.reviewEventMain,
  description: "스튜디오가 업로드한 후기 이벤트 대표 사진입니다.",
};

const REVIEW_EVENT_PHOTOS = [
  {
    title: "후기 이벤트 대표 컷",
    branch: "MY STUDIO",
    date: "2026.06.25",
    image: ASSETS.reviewEventMain,
    description: "스튜디오가 업로드한 후기 이벤트 대표 사진입니다.",
  },
  {
    title: "가족 촬영 후기 컷",
    branch: "강남점",
    date: "2026.06.25",
    image: HOME_DUMMY_IMAGES.familyGarden,
    description: "가족 촬영 후기에 사용할 수 있는 더미 사진입니다.",
  },
  {
    title: "100일 촬영 후기 컷",
    branch: "분당점",
    date: "2026.06.25",
    image: HOME_DUMMY_IMAGES.babyCrown,
    description: "100일 촬영 후기에 사용할 수 있는 더미 사진입니다.",
  },
];

const REVIEW_PHOTO_SESSION_KEY = "figmaHomeSelectedReviewPhoto";
const HOME_APPLIED_EVENTS_SESSION_KEY = "figmaHomeAppliedStoreEvents";
const HOME_EVENT_APPLICATIONS_STORAGE_KEY = "storeEventApplications.v1";

const REVIEW_EVENT_STATUS = {
  loading: "loading",
  notApplied: "not-applied",
  processing: "processing",
  ready: "ready",
  unavailable: "unavailable",
};

const POPULAR_THEMES = [
  { rank: 1, title: "화이트 가든 패밀리", image: HOME_DUMMY_IMAGES.familyGarden },
  { rank: 2, title: "퓨어 윈도우 베이비", image: HOME_DUMMY_IMAGES.pureWindow },
  { rank: 3, title: "핑크 생일 파티", image: HOME_DUMMY_IMAGES.birthdayPink },
];

const NOTICE_CENTER_TABS = [
  { label: "전체", route: "/faqList?tab=all", active: true },
  { label: "공지", route: "/faqList?tab=notice" },
  { label: "FAQ", route: "/faqList?tab=faq" },
  { label: "내 문의", route: "/qnaList?screen=inquiry" },
];

const NOTICE_CENTER_ITEMS = [
  {
    type: "status",
    badge: "답변완료",
    title: "앨범 사진 변경 문의",
    meta: "1:1 문의 · 2026.06.30",
    route: "/qnaList?screen=inquiry",
  },
  {
    type: "notice",
    badge: "공지",
    title: "촬영 전 준비사항을 확인해주세요",
    meta: "촬영안내 · 2026.06.30",
    route: "/faqList?tab=notice",
  },
  {
    type: "faq",
    badge: "FAQ",
    title: "원본사진은 어디서 다운로드하나요?",
    meta: "사진/원본 · 2026.06.30",
    route: "/faqList?tab=faq",
  },
];


function getDownloadFileName(title = "studio-review-photo") {
  return `${String(title).replace(/[\\/:*?"<>|#%&{}$!'@+=`]/g, "-") || "studio-review-photo"}.png`;
}

async function downloadImageToLocal(imageUrl, title) {
  if (!imageUrl) return;

  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    if (!response.ok) {
      throw new Error("image download failed");
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = getDownloadFileName(title);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = getDownloadFileName(title);
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

function loadAppliedStoreEventIds() {
  if (typeof window === "undefined") return [];

  try {
    const customerId = getCurrentCustomerId();
    const { storage, key } = getCustomerScopedStorage(HOME_EVENT_APPLICATIONS_STORAGE_KEY);
    const scopedValue = storage?.getItem(key);
    const legacyValue = customerId <= 0
      ? window.sessionStorage.getItem(HOME_APPLIED_EVENTS_SESSION_KEY)
      : null;
    const storedValue = scopedValue || legacyValue || "[]";
    const storedIds = JSON.parse(storedValue);

    if (storage && legacyValue && !scopedValue) {
      storage.setItem(key, JSON.stringify(storedIds));
      window.sessionStorage.removeItem(HOME_APPLIED_EVENTS_SESSION_KEY);
    }

    return Array.isArray(storedIds) ? storedIds.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveAppliedStoreEventIds(eventIds) {
  if (typeof window === "undefined") return;

  try {
    const { storage, key } = getCustomerScopedStorage(HOME_EVENT_APPLICATIONS_STORAGE_KEY);
    storage?.setItem(key, JSON.stringify(eventIds));
  } catch {
    // Storage may be unavailable in some embedded browser modes.
  }
}

function resolveReviewEventStatus({ loading, applicationRows, uploadedRows, previewState }) {
  const previewStates = Object.values(REVIEW_EVENT_STATUS);
  if (process.env.NODE_ENV === "development" && previewStates.includes(previewState)) return previewState;
  if (loading) return REVIEW_EVENT_STATUS.loading;
  if (Array.isArray(uploadedRows) && uploadedRows.length > 0) return REVIEW_EVENT_STATUS.ready;

  const hasApplication = Array.isArray(applicationRows)
    && applicationRows.some((row) => String(row?.joined_event || "").toUpperCase() === "Y");

  return hasApplication ? REVIEW_EVENT_STATUS.processing : REVIEW_EVENT_STATUS.notApplied;
}

function saveReviewPhotoSelection(photo) {
  const nextPhoto = photo?.id ? photo : null;
  if (!nextPhoto) return null;

  try {
    const { storage, key } = getCustomerScopedStorage(REVIEW_PHOTO_SESSION_KEY);
    storage?.setItem(key, JSON.stringify(nextPhoto));
  } catch {
    // Storage may be unavailable in some embedded browser modes.
  }

  return nextPhoto;
}

function getReviewPhotoSelection() {
  try {
    const { storage, key } = getCustomerScopedStorage(REVIEW_PHOTO_SESSION_KEY);
    const storedValue = storage?.getItem(key);
    if (storedValue) {
      const storedPhoto = JSON.parse(storedValue);
      if (storedPhoto?.id) return storedPhoto;
    }
  } catch {
    // Fall back to the representative review photo.
  }

  return null;
}

function buildReviewPost(photo) {
  if (!photo) return null;
  const description = photo.description || `스튜디오에서 등록한 ${photo.category || "촬영"} 사진입니다.`;
  const images = Array.isArray(photo.images) && photo.images.length > 0
    ? photo.images
    : photo.image ? [{
        src: photo.image,
        title: photo.title || "스튜디오 갤러리 사진",
        caption: description,
      }] : [];
  return {
    id: photo.id || photo.title || "studio-review-post",
    title: photo.title || "스튜디오 갤러리 사진",
    branch: photo.branch || "",
    date: photo.date || "",
    curator: "MY STUDIO 업로드",
    theme: photo.category || "",
    packageName: "",
    description,
    tags: [photo.category].filter(Boolean),
    comments: [],
    images,
  };
}

function GalleryImage({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(!src);

  useEffect(() => {
    setFailed(!src);
  }, [src]);

  if (failed) {
    return (
      <span className={`figma-home-gallery-pending ${className}`.trim()} role="img" aria-label={`${alt || "사진"} 준비 중`}>
        <i aria-hidden="true">♡</i>
        <strong>사진 준비 중</strong>
        <small>스튜디오에서 곧 사진을 등록할 예정이에요.</small>
      </span>
    );
  }

  return <img className={className} src={src} alt={alt} onError={() => setFailed(true)} />;
}

function formatCount(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return value;
  return numberValue.toLocaleString("ko-KR");
}

function formatScheduleDate(value) {
  if (!value) return "일정 미정";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).replace(/-/g, ".");
  const day = ["일", "월", "화", "수", "목", "금", "토"][date.getDay()];
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}(${day})`;
}

function parseScheduleDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const text = String(value).trim();
  const matched = text.match(/(\d{4})[-.\/](\d{1,2})[-.\/](\d{1,2})/);

  if (matched) {
    const [, year, month, day] = matched;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function makeDdayInfo(daysUntil) {
  if (!Number.isFinite(daysUntil)) {
    return {
      label: "일정 확인",
      caption: "촬영 일정",
      tone: "relaxed",
      message: "촬영일을 확인해 주세요",
    };
  }

  if (daysUntil < 0) {
    return {
      label: "촬영 완료",
      caption: `${Math.abs(daysUntil)}일 지남`,
      tone: "completed",
      message: "촬영이 완료되었어요",
    };
  }

  if (daysUntil === 0) {
    return {
      label: "D-DAY",
      caption: "오늘 촬영",
      tone: "today",
      message: "오늘 촬영이에요",
    };
  }

  const tone = daysUntil <= 6 ? "urgent" : daysUntil <= 29 ? "soon" : "relaxed";

  return {
    label: `D-${daysUntil}`,
    caption: "촬영까지",
    tone,
    message: `${daysUntil}일 남았어요`,
  };
}

function getScheduleDdayInfo(dateValue, dDayValue) {
  const date = parseScheduleDate(dateValue);

  if (date) {
    const diffMs = startOfLocalDay(date).getTime() - startOfLocalDay(new Date()).getTime();
    return makeDdayInfo(Math.round(diffMs / 86400000));
  }

  const numericDday = Number(dDayValue);
  if (Number.isFinite(numericDday)) return makeDdayInfo(numericDday);

  return makeDdayInfo(NaN);
}

function mapSchedule(row) {
  if (!row) return null;
  const scheduleDate = row.scheduledAt || "";
  const dDayInfo = getScheduleDdayInfo(scheduleDate);
  const scheduleTime = scheduleDate ? new Date(scheduleDate) : null;
  const themeSelection = row.themeSelection || {};
  const selectedThemeIds = Array.isArray(themeSelection.selectedThemeIds)
    ? themeSelection.selectedThemeIds
    : [];

  return {
    id: String(row.id || row.scheduleId || ""),
    badge: selectedThemeIds.length > 0 ? "테마선택 완료" : "촬영 예정",
    rawDate: scheduleDate,
    dDay: dDayInfo.label,
    dDayInfo,
    packageName: row.productTitle || "상품 정보 없음",
    title: row.title || "촬영 일정",
    date: formatScheduleDate(scheduleDate),
    time: !scheduleTime || Number.isNaN(scheduleTime.getTime())
      ? "시간 미정"
      : `${String(scheduleTime.getHours()).padStart(2, "0")}:${String(scheduleTime.getMinutes()).padStart(2, "0")}`,
    theme: "",
    studio: row.office?.name || "스튜디오 정보 없음",
    type: row.shootType || "촬영 종류 미정",
    thumbnail: row.thumbnailUrl || "",
    themeSelectionRequired: Boolean(themeSelection.required),
    themeSelectionComplete: Boolean(
      themeSelection.locked
      || (
        Number(themeSelection.requiredCount) > 0
        && selectedThemeIds.length >= Number(themeSelection.requiredCount)
      )
    ),
    themeRequiredTotal: Math.max(1, Number(themeSelection.requiredCount) || 1),
  };
}

function mapRecentShooting(row) {
  if (!row) return null;
  const scheduleTime = row.scheduledAt ? new Date(row.scheduledAt) : null;

  return {
    id: String(row.id || row.scheduleId || ""),
    badge: "촬영 완료",
    packageName: row.productTitle || "상품 정보 없음",
    title: row.title || "촬영 일정",
    date: formatScheduleDate(row.scheduledAt),
    time: !scheduleTime || Number.isNaN(scheduleTime.getTime())
      ? "시간 미정"
      : `${String(scheduleTime.getHours()).padStart(2, "0")}:${String(scheduleTime.getMinutes()).padStart(2, "0")}`,
    studio: row.office?.name || "스튜디오 정보 없음",
    type: row.shootType || "촬영 종류 미정",
  };
}
function getPhotoThumbnail(row) {
  return (
    row?.url ||
    row?.thumbnailUrl ||
    row?.thumbnail?.small_url ||
    row?.thumbnail?.middle_url ||
    row?.thumbnail?.org_url ||
    row?.img_url?.small_url ||
    row?.img_url?.middle_url ||
    row?.img_url?.org_url ||
    row?.image_url?.small_url ||
    ""
  );
}

function formatReviewDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10).replace(/-/g, ".");
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

function mapReviewPhoto(row, index) {
  if (!row) return null;
  const fallback = { title: "스튜디오 갤러리 사진", branch: "", date: "", image: "", category: "기타" };
  const category =
    row.category ||
    row.category_name ||
    row.photo_category ||
    row.theme_category ||
    row.theme_type ||
    fallback.category;

  return {
    id: String(row.id || row.photoId || row.photo_id || index || ""),
    title: row.title || row.schedule_title || row.theme_name || fallback.title,
    branch: row.officeName || row.office_name || row.branch_name || fallback.branch,
    date: formatReviewDate(row.uploadedAt || row.review_dt || row.created_at || row.insert_dt || row.reg_dt || row.date || row.schedule_date || fallback.date),
    image: getPhotoThumbnail(row) || fallback.image,
    category,
    sortOrder: Number(row.sortOrder ?? row.sort_order ?? index ?? 0),
  };
}

function mapGalleryPostCard(post) {
  if (!post) return null;
  return {
    id: post.id,
    galleryPostId: post.id,
    title: post.title,
    description: post.summary,
    author: post.authorDisplayName || "MyStudio",
    branch: post.officeName,
    category: post.category?.name || "기타",
    categoryId: post.category?.id || "",
    date: formatReviewDate(post.publishedAt || post.createdAt),
    image: post.coverPhoto?.thumbnailUrl || post.coverPhoto?.imageUrl || "",
    photoCount: post.photoCount,
    viewCount: post.viewCount || 0,
    sortOrder: post.sortOrder,
    images: post.coverPhoto ? [{
      src: post.coverPhoto.imageUrl || post.coverPhoto.thumbnailUrl || "",
      title: post.title,
      caption: post.summary || post.category?.name || "",
    }] : [],
  };
}

function formatGalleryCategoryLabel(value) {
  const categoryName = String(value || "").trim().replace(/\s*갤러리$/, "");
  if (categoryName === "헤리티지 한옥") return "한옥사진";
  if (categoryName === "주니어사진") return "주니어";
  return categoryName || "기타";
}

function formatStudioBranchLabel(value) {
  const branchName = String(value || "").trim();
  if (!branchName) return "스튜디오";
  return branchName.endsWith("점") ? branchName : `${branchName}점`;
}

function Icon({ name }) {
  if (name === "calendar") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" /></svg>;
  }

  if (name === "clock") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
  }

  if (name === "sparkle") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3ZM19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15Z" /></svg>;
  }

  if (name === "bag") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 7h12l1 13H5L6 7Z" /><path d="M9 7a3 3 0 0 1 6 0" /></svg>;
  }

  if (name === "user") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
  }

  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10" /></svg>;
}

function HomeGraphicIcon({ name }) {
  const icons = {
    theme: <><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" /><path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15Z" /></>,
    photo: <><rect x="4" y="5" width="16" height="14" rx="2" /><path d="m7 16 3.4-3.4a1.4 1.4 0 0 1 2 0L16 16" /><path d="M15 10h5M17.5 7.5v5" /><circle cx="9" cy="9" r="1.3" /></>,
    progress: <><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3 2" /><path d="M4 4l3 3M20 4l-3 3" /></>,
    prep: <><path d="M4 14h3l8 4V6L7 10H4v4Z" /><path d="M7 14v4" /><path d="M18 9.5a3 3 0 0 1 0 5" /></>,
    chat: <><path d="M5 6h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H10l-5 4v-4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" /><path d="M8 10h8M8 13h5" /></>,
    notice: <><path d="M7 3h7l4 4v14H7V3Z" /><path d="M14 3v5h5" /><path d="M9.5 12h5M9.5 16h5" /></>,
    faq: <><circle cx="12" cy="12" r="9" /><path d="M9.7 9a2.5 2.5 0 0 1 4.8.9c0 2.2-2.8 2.1-2.8 4.1" /><path d="M12 17.2h.01" /></>,
    welcome: <><path d="M5 8h3l2-3h4l2 3h3a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3Z" /><circle cx="12" cy="14" r="4" /><path d="M19 11h.01" /></>,
    support: <><path d="M4 12a8 8 0 0 1 16 0v4a3 3 0 0 1-3 3h-2" /><path d="M6 13H4v4h2a2 2 0 0 0 2-2v0a2 2 0 0 0-2-2Z" /><path d="M18 13h2v4h-2a2 2 0 0 1-2-2v0a2 2 0 0 1 2-2Z" /><path d="M10 20h4" /></>,
    bell: <><path d="M18 9.8a6 6 0 1 0-12 0c0 7-2.6 7-2.6 8.2h17.2C20.6 16.8 18 16.8 18 9.8Z" /><path d="M9.7 20a2.6 2.6 0 0 0 4.6 0" /></>,
  };

  return (
    <span className={`figma-home-svg-icon figma-home-svg-icon--${name}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">{icons[name] || icons.notice}</svg>
    </span>
  );
}
function StatusBar() {
  return (
    <div className="figma-home-status" aria-hidden="true">
      <span>9:41</span>
      <span className="figma-home-status__icons"><i /><i /><i /></span>
    </div>
  );
}

function HomeLogo() {
  return (
    <span className="figma-home-logo" aria-label="My studio">
      <span className="figma-home-logo__mark" aria-hidden="true">
        <img alt="" src="/static/img/mobile-header/header-logo-mark.svg" />
      </span>
      <img className="figma-home-logo__text" alt="My studio" src="/static/img/mobile-header/header-logo-text.svg" />
    </span>
  );
}

function HomeHeader({ onBellClick }) {
  return (
    <header className="figma-home-header" data-node-id="7159:9351">
      <HomeLogo />
      <button className="figma-home-header__bell" type="button" aria-label="알림" onClick={onBellClick}>
        <img alt="" src="/static/img/mobile-header/header-bell.svg" />
      </button>
    </header>
  );
}

function WelcomeCard({ userName, title = "환영합니다!", description = "우리 가족의 이야기를 함께 만들어가요.\n스케줄이 배정되면 이곳에서 안내해드릴게요." }) {
  return (
    <section className="figma-home-welcome" data-node-id="7133:1048">
      <div className="figma-home-welcome__copy">
        <h1>{userName ? `${userName} 고객님,` : "고객님,"}<strong>{title}</strong></h1>
        <p>{description}</p>
      </div>
      <img className="figma-home-welcome__object" alt="" src={ASSETS.welcomeObject} />
    </section>
  );
}

const TODAY_TASK_ICONS = {
  download: ASSETS.recentDownload,
  theme: ASSETS.statTheme,
  photo: ASSETS.statPhoto,
};

function getTodayTaskDeadlineLabel(item) {
  if (typeof item?.ddayLabel === "string" && item.ddayLabel) return item.ddayLabel;
  if (!Number.isFinite(item?.remainingDays)) return "";
  if (item.remainingDays < 0) return "기한 지남";
  if (item.remainingDays === 0) return "D-DAY";
  return `D-${item.remainingDays}`;
}

function TodayTaskDashboard({
  items = [],
  loading,
  error,
  partial,
  title = "오늘 할 일",
  description = "",
  onOpenAll,
  onOpenFilter,
  onOpenTask,
  onRetry,
}) {
  const counts = getTodayTaskCounts(items);
  const urgentTask = getUrgentTodayTask(items);
  const urgentDeadline = getTodayTaskDeadlineLabel(urgentTask);

  return (
    <section className="figma-home-today-dashboard" aria-labelledby="home-today-task-title">
      <button
        className="figma-home-today-dashboard__header"
        type="button"
        aria-label={`오늘 할 일 ${loading ? "확인 중" : error ? "불러오기 오류" : `${counts.all}개`} 전체보기`}
        onClick={onOpenAll}
      >
        <span className="figma-home-today-dashboard__heading">
          <span>TODAY</span>
          <strong id="home-today-task-title">{title}</strong>
          {description ? <small>{description}</small> : null}
        </span>
        <span className="figma-home-today-dashboard__header-action">
          <span className="figma-home-today-dashboard__count" aria-hidden="true">
            {loading ? "…" : error ? "!" : formatCount(counts.all)}
          </span>
          <span className="figma-home-today-dashboard__all">전체보기</span>
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5" /></svg>
        </span>
      </button>

      {loading ? (
        <div className="figma-home-today-dashboard__loading" role="status">
          오늘 할 일을 확인하고 있어요.
        </div>
      ) : error ? (
        <div className="figma-home-today-dashboard__error" role="status">
          <span>할 일을 확인하지 못했어요.</span>
          <button type="button" onClick={onRetry}>다시 확인</button>
        </div>
      ) : counts.all === 0 ? (
        <div className="figma-home-today-dashboard__complete" role="status">
          <span aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="m6 12 4 4 8-9" /></svg>
          </span>
          <div>
            <strong>오늘 할 일을 모두 완료했어요</strong>
            <small>새로운 할 일이 생기면 바로 알려드릴게요.</small>
          </div>
        </div>
      ) : (
        <>
          <div className="figma-home-today-dashboard__categories" aria-label="할 일 종류">
            {TODAY_TASK_FILTERS.map((filter) => (
              <button
                className="figma-home-today-dashboard__category"
                type="button"
                key={filter.key}
                aria-label={`${filter.label} ${counts[filter.key]}개 보기`}
                onClick={() => onOpenFilter(filter.key)}
              >
                <span className="figma-home-today-dashboard__category-icon" aria-hidden="true">
                  <img alt="" src={TODAY_TASK_ICONS[filter.key]} />
                </span>
                <span className="figma-home-today-dashboard__category-count">
                  <strong>{formatCount(counts[filter.key])}</strong>개
                </span>
                <small>{filter.label}</small>
              </button>
            ))}
          </div>

          {urgentTask ? (
            <button
              className="figma-home-today-dashboard__urgent"
              type="button"
              aria-label={`긴급 할 일 ${urgentTask.title} 확인`}
              onClick={() => onOpenTask(urgentTask)}
            >
              <span>{urgentDeadline ? `긴급 · ${urgentDeadline}` : "긴급"}</span>
              <strong>{urgentTask.title}</strong>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5" /></svg>
            </button>
          ) : null}

          {partial ? (
            <div className="figma-home-today-dashboard__partial" role="status">
              <span>일부 할 일을 확인하지 못했어요.</span>
              <button type="button" onClick={onRetry}>다시 확인</button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function TodayTasksPage({
  items = [],
  filterKey,
  actionId,
  loading,
  dataStatus,
  isPreview,
  onBack,
  onShowAll,
  onAction,
  onSnooze,
  onRetry,
  onNavigate,
}) {
  const filteredItems = filterTodayTaskItems(items, { filterKey, actionId });
  const selectedTask = actionId ? items.find((item) => String(item?.id) === String(actionId)) : null;
  const activeFilter = selectedTask
    ? TODAY_TASK_FILTERS.find((filter) => filter.type === selectedTask.type) || null
    : getTodayTaskFilter(filterKey);
  const isScoped = Boolean(actionId || activeFilter);
  const hasEmptyLoadError = filteredItems.length === 0 && (dataStatus === "partial" || dataStatus === "error");
  const heading = selectedTask?.title
    || (activeFilter ? `${activeFilter.label} 할 일` : actionId ? "선택한 할 일" : "완료해야 할 일");

  return (
    <section className="figma-home-page figma-home-tasks-page mobile-app-frame">
      <StatusBar />
      <HomeSubHeader title="오늘 할 일" onBack={onBack} />
      <main className="figma-home-tasks-scroll">
        <section className="figma-home-tasks-intro">
          <div className="figma-home-tasks-intro__copy">
            <span>TODAY</span>
            <h2 id={TODAY_TASK_LIST_TITLE_ID}>{heading}</h2>
            <p>필요한 내용을 확인하고 바로 이어서 진행해 보세요.</p>
          </div>
          <span className="figma-home-tasks-intro__count" aria-label={`표시된 할 일 ${filteredItems.length}개`}>
            {loading ? "…" : filteredItems.length}
          </span>
        </section>

        {isScoped ? (
          <button className="figma-home-tasks-show-all" type="button" onClick={onShowAll}>
            전체 할 일 보기
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5" /></svg>
          </button>
        ) : null}

        {isPreview ? (
          <div className="figma-home-action-data-note is-preview" role="status">
            LOCAL 환경에서 기획 확인용 할 일을 보여드리고 있어요.
          </div>
        ) : dataStatus === "partial" || dataStatus === "error" ? (
          <div className="figma-home-action-data-note" role="status">
            <span>일부 해야 할 일을 불러오지 못했어요.</span>
            <button type="button" onClick={onRetry}>다시 불러오기</button>
          </div>
        ) : null}

        {loading ? (
          <div className="figma-home-tasks-state" role="status">오늘 할 일을 불러오고 있어요.</div>
        ) : filteredItems.length > 0 ? (
          <ActionCenter
            className="figma-home-tasks-action-center"
            items={filteredItems}
            labelledBy={TODAY_TASK_LIST_TITLE_ID}
            onAction={onAction}
            onSnooze={onSnooze}
            showHeader={false}
          />
        ) : (
          <div className="figma-home-tasks-empty" role="status">
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="m6 12 4 4 8-9" /></svg>
            </span>
            <strong>
              {hasEmptyLoadError
                ? "할 일을 모두 확인하지 못했어요"
                : isScoped && items.length > 0
                  ? "해당 할 일을 모두 확인했어요"
                  : "오늘 할 일을 모두 완료했어요"}
            </strong>
            <p>
              {hasEmptyLoadError
                ? "잠시 후 다시 불러와 주세요."
                : isScoped && items.length > 0
                  ? "전체 할 일에서 다른 항목을 확인할 수 있어요."
                  : "새로운 할 일이 생기면 홈에서 바로 알려드릴게요."}
            </p>
          </div>
        )}
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
    </section>
  );
}

function SectionTitle({ children, copy, actionLabel, actionAriaLabel, onAction }) {
  return (
    <div className="figma-home-section-title">
      <div className="figma-home-section-title__row">
        <h2>{children}</h2>
        {actionLabel ? <button type="button" aria-label={actionAriaLabel || actionLabel} onClick={onAction}>{actionLabel}<i aria-hidden="true">›</i></button> : null}
      </div>
      {copy ? <p>{copy}</p> : null}
    </div>
  );
}

function UpcomingIcon({ name }) {
  if (name === "time") {
    return (
      <span className="figma-home-upcoming-card__icon figma-home-upcoming-card__icon--time" aria-hidden="true">
        <img src={ASSETS.upcomingTimeFace} alt="" />
        <img src={ASSETS.upcomingTimeHand} alt="" />
      </span>
    );
  }

  const iconMap = {
    calendar: ASSETS.upcomingCalendar,
    location: ASSETS.upcomingLocation,
    camera: ASSETS.upcomingCamera,
  };

  return <img className="figma-home-upcoming-card__icon" src={iconMap[name]} alt="" aria-hidden="true" />;
}

function UpcomingInfoLine({ icon, children }) {
  return <p className="figma-home-upcoming-card__line"><UpcomingIcon name={icon} /><span>{children}</span></p>;
}

function PrepGuideCard({ onClick }) {
  return (
    <button className="figma-home-prep" type="button" data-node-id="6396:27374" onClick={onClick}>
      <span className="figma-home-prep__inner" data-node-id="6396:27396">
        <span className="figma-home-prep__content" data-node-id="6396:27407">
          <span className="figma-home-prep__thumb" aria-hidden="true" data-node-id="6396:27375">
            <img src={ASSETS.prepGuideMegaphone} alt="" data-node-id="6396:27410" />
          </span>
          <span className="figma-home-prep__copy" data-node-id="6396:27395">
            <strong data-node-id="6396:27385">촬영 전 준비사항 안내</strong>
            <small data-node-id="6396:27378">촬영 당일 더욱 편하고 완벽하게 보내실 수 있도록<br />준비사항을 안내해드려요.</small>
          </span>
        </span>
        <img className="figma-home-prep__chevron" src={ASSETS.prepGuideChevron} alt="" aria-hidden="true" data-node-id="6396:27405" />
      </span>
    </button>
  );
}

function UpcomingScheduleSection({
  schedule,
  unavailable = false,
  loading = false,
  title = "예정된 촬영일정",
  description = "",
  onScheduleClick,
  onPrepClick,
}) {
  if (!schedule) {
    return (
      <section className="figma-home-schedule" data-node-id="7184:3450">
        <SectionTitle copy={description}>{title}</SectionTitle>
        <MystudioDataState
          type={loading ? "loading" : "empty"}
          compact
          title={unavailable ? "촬영일정을 확인하고 있어요" : "예정된 촬영일정이 없어요"}
          description={unavailable ? "연결이 복구되면 고객님의 실제 예약 일정을 표시합니다." : "스튜디오에서 일정을 등록하면 이곳에 표시됩니다."}
        />
      </section>
    );
  }
  const dDayInfo = schedule.dDayInfo || getScheduleDdayInfo(schedule.rawDate || schedule.date, schedule.dDay);

  return (
    <section className="figma-home-schedule" data-node-id="7184:3450">
      <SectionTitle copy={description}>{title}</SectionTitle>
      <div className="figma-home-upcoming-stack" data-node-id="7184:3457">
        <article className="figma-home-upcoming-card" data-node-id="7184:3458">
          <div className="figma-home-upcoming-card__body" data-node-id="7184:3459">
            <div className="figma-home-upcoming-card__top">
              <span className={`figma-home-upcoming-card__dday figma-home-upcoming-card__dday--${dDayInfo.tone}`}>
                <strong>{dDayInfo.label}</strong>
                <small>{dDayInfo.caption}</small>
              </span>
              <span className="figma-home-upcoming-card__summary">
                <p className="figma-home-upcoming-card__package" data-node-id="7184:3463">{schedule.packageName}</p>
                <h3 data-node-id="7184:3466">{schedule.title}</h3>
              </span>
            </div>
            <div className="figma-home-upcoming-card__lines" data-node-id="7184:3467">
              <UpcomingInfoLine icon="calendar">{schedule.date}</UpcomingInfoLine>
              <UpcomingInfoLine icon="time">{schedule.time}</UpcomingInfoLine>
              <UpcomingInfoLine icon="location">{schedule.studio}</UpcomingInfoLine>
              <UpcomingInfoLine icon="camera">{schedule.type}</UpcomingInfoLine>
            </div>
            <button className="figma-home-upcoming-card__theme" type="button" onClick={onScheduleClick} data-node-id="7184:3480">
              <span>{schedule.themeSelectionComplete ? "테마선택 완료" : "테마선택"}</span>
              <img src={ASSETS.upcomingChevronOrange} alt="" aria-hidden="true" />
            </button>
          </div>
        </article>
        <PrepGuideCard onClick={onPrepClick} />

      </div>
    </section>
  );
}

function RecentIcon({ name }) {
  if (name === "time") {
    return (
      <span className="figma-home-recent-card__icon figma-home-recent-card__icon--time" aria-hidden="true">
        <img src={ASSETS.recentTimeFace} alt="" />
        <img src={ASSETS.recentTimeHand} alt="" />
      </span>
    );
  }

  const iconMap = {
    calendar: ASSETS.recentCalendar,
    location: ASSETS.recentLocation,
    camera: ASSETS.recentCamera,
  };

  return <img className="figma-home-recent-card__icon" src={iconMap[name]} alt="" aria-hidden="true" />;
}

function RecentInfoLine({ icon, children }) {
  return <p className="figma-home-recent-card__line"><RecentIcon name={icon} /><span>{children}</span></p>;
}

function RecentShootingSection({ schedule, unavailable = false, loading = false, title = "최근 촬영사진", description = "", onViewAll, onDownload }) {
  if (!schedule) {
    return (
      <section className="figma-home-recent" data-node-id="7184:3395">
        <SectionTitle copy={description}>{title}</SectionTitle>
        <MystudioDataState
          type={loading ? "loading" : "empty"}
          compact
          title={unavailable ? "고객님의 촬영사진을 확인 중이에요" : "최근 완료된 촬영이 없어요"}
          description={unavailable ? "연결 전에는 촬영 완료 여부와 원본사진을 확인할 수 없어요." : "촬영이 완료되면 사진 정보를 확인할 수 있어요."}
        />
      </section>
    );
  }
  return (
    <section className="figma-home-recent" data-node-id="7184:3395">
      <div className="figma-home-recent__header" data-node-id="7184:3396">
        <span>
          <h2 data-node-id="7184:3401">{title}</h2>
          {description ? <p>{description}</p> : null}
        </span>
        <button type="button" onClick={onViewAll} data-node-id="7184:3402">
          <span>전체보기</span>
          <img src={ASSETS.recentChevron} alt="" aria-hidden="true" />
        </button>
      </div>
      <article className="figma-home-recent-card" data-node-id="7184:3405">
        <div className="figma-home-recent-card__body">
          <span className="figma-home-recent-card__badge" data-node-id="7184:3407">{schedule.badge}</span>
          <p className="figma-home-recent-card__package" data-node-id="7184:3410">{schedule.packageName}</p>
          <h3 data-node-id="7184:3413">{schedule.title}</h3>
          <div className="figma-home-recent-card__lines" data-node-id="7184:3414">
            <RecentInfoLine icon="calendar">{schedule.date}</RecentInfoLine>
            <RecentInfoLine icon="time">{schedule.time}</RecentInfoLine>
            <RecentInfoLine icon="location">{schedule.studio}</RecentInfoLine>
            <RecentInfoLine icon="camera">{schedule.type}</RecentInfoLine>
          </div>
          <button className="figma-home-recent-card__download" type="button" onClick={onDownload} data-node-id="7184:3427">
            <img src={ASSETS.recentDownload} alt="" aria-hidden="true" />
            <span>사진 원본 다운로드</span>
          </button>
        </div>
      </article>
    </section>
  );
}
function ReviewPhotoSection({ onClick }) {
  return (
    <section className="figma-home-review-photo" data-node-id="6396:27174">
      <div className="figma-home-review-photo__header" data-node-id="6396:26960">
        <h2 data-node-id="6396:26965">후기 이벤트 사진</h2>
        <p data-node-id="6396:26975">스튜디오가 업로드한 보정사진 3컷을 확인해보세요.</p>
      </div>
      <div className="figma-home-photo-carousel" aria-label="후기 이벤트 사진" data-node-id="6743:14579">
        <span className="figma-home-photo-carousel__side is-left" aria-hidden="true" data-node-id="6396:27171" />
        <button className="is-active" type="button" onClick={onClick} data-node-id="6396:26977">
          <img src={ASSETS.reviewEventMain} alt="후기 이벤트 사진" data-node-id="6743:14574" />
        </button>
        <span className="figma-home-photo-carousel__side is-right" aria-hidden="true" data-node-id="6396:27170" />
        <span className="figma-home-photo-carousel__page" data-node-id="6743:14576"><strong>1/</strong><em>3</em></span>
      </div>
    </section>
  );
}

function ReviewEventPhotoSection({
  status,
  photos,
  title = "후기 이벤트 사진",
  description = "신청 상태에 따라 진행 내용을 안내해드려요.",
  onClick,
  onApply,
}) {
  const displayPhotos = Array.isArray(photos) ? photos : [];
  const handlePhotoClick = (photo) => {
    if (typeof onClick === "function") {
      onClick(photo);
    }
  };

  if (status !== REVIEW_EVENT_STATUS.ready) {
    const isLoading = status === REVIEW_EVENT_STATUS.loading;
    const isProcessing = status === REVIEW_EVENT_STATUS.processing;
    const isUnavailable = status === REVIEW_EVENT_STATUS.unavailable;
    const stateLabel = isLoading ? "상태 확인 중" : isProcessing ? "신청 완료" : isUnavailable ? "준비 중" : "신청 전";
    const title = isLoading
      ? "후기 이벤트 상태를 확인하고 있어요"
      : isProcessing
        ? "스튜디오에서 보정사진 3컷을 준비하고 있어요"
        : isUnavailable
          ? "후기 이벤트를 준비하고 있어요"
        : "후기 이벤트를 신청하고 보정사진 3컷을 받아보세요";
    const description = isLoading
      ? "잠시만 기다려 주세요."
      : isProcessing
        ? "사진 업로드가 완료되면 알림으로 바로 알려드릴게요."
        : isUnavailable
          ? "스튜디오에서 이벤트를 등록하면 이곳에서 신청할 수 있어요."
        : "촬영 원본 중 스튜디오가 선별·보정한 3컷을 업로드해드려요.";

    return (
      <section className="figma-home-review-photo figma-home-review-photo--state" data-node-id="6396:27174">
        <div className="figma-home-review-photo__header">
          <h2>{title}</h2>
          <p>{description || (isProcessing ? "신청이 완료되어 사진을 준비하고 있어요." : "신청 상태에 따라 진행 내용을 안내해드려요.")}</p>
        </div>
        <div
          className={`figma-home-review-event-state is-${status}`}
          role={isLoading || isProcessing ? "status" : undefined}
          aria-live={isLoading || isProcessing ? "polite" : undefined}
        >
          <span className="figma-home-review-event-state__visual" aria-hidden="true">
            <img src={ASSETS.reviewEventMain} alt="" />
            <i>{isProcessing ? "···" : isLoading ? "…" : "+"}</i>
          </span>
          <div className="figma-home-review-event-state__copy">
            <span className="figma-home-review-event-state__badge">{stateLabel}</span>
            <h3>{title}</h3>
            <p>{description}</p>
            {isLoading ? (
              <div className="figma-home-review-event-state__loading" aria-hidden="true"><i /><i /><i /></div>
            ) : isProcessing ? (
              <div className="figma-home-review-event-state__steps" aria-label="후기 이벤트 진행 단계">
                <span className="is-done">신청 완료</span>
                <span className="is-active">보정 중</span>
                <span>업로드</span>
              </div>
            ) : isUnavailable ? null : (
              <button type="button" onClick={onApply}>후기 이벤트 신청하기</button>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="figma-home-review-photo" data-node-id="6396:27174">
      <div className="figma-home-review-photo__header" data-node-id="6396:26960">
        <h2 data-node-id="6396:26965">{title}</h2>
        <p data-node-id="6396:26975">{description || "스튜디오가 업로드한 후기 사진 3컷을 확인해보세요."}</p>
      </div>
      {displayPhotos.length === 0 ? (
        <MystudioDataState
          type="empty"
          compact
          title="업로드된 후기 이벤트 사진이 없어요"
          description="스튜디오에서 사진을 업로드하면 이곳에서 확인할 수 있어요."
        />
      ) : <div className="figma-home-review-photo__grid" aria-label={`후기 이벤트 사진 ${displayPhotos.length}컷`} data-node-id="6743:14579">
        {displayPhotos.map((photo, index) => (
          <button
            key={photo.title}
            className="figma-home-review-photo__card"
            type="button"
            onClick={() => handlePhotoClick(photo)}
            data-node-id={index === 0 ? "6396:26977" : undefined}
          >
            <img src={photo.image} alt={photo.title} data-node-id={index === 0 ? "6743:14574" : undefined} />
            <span>
              <em>{index + 1}/{displayPhotos.length}</em>
              <strong>{photo.title}</strong>
            </span>
          </button>
        ))}
      </div>}
    </section>
  );
}

function ReviewPhotoActionCard({ photos, onClick }) {
  const previewPhotos = photos.slice(0, 3);

  return (
    <button className="figma-home-photo-action" type="button" data-node-id="6396:27253" onClick={onClick}>
      <span className="figma-home-photo-action__thumbs" aria-hidden="true">
        {previewPhotos.map((photo, index) => (
          <img key={`${photo.title}-${index}`} src={photo.image} alt="" />
        ))}
      </span>
      <span className="figma-home-photo-action__copy">
        <span className="figma-home-photo-action__label">후기 이벤트 사진</span>
        <strong>{previewPhotos.length}컷 확인하기</strong>
        <small>선택된 사진과 후기 이벤트 신청 상태를 확인할 수 있어요.</small>
      </span>
      <i aria-hidden="true">›</i>
    </button>
  );
}

function StoreEventSection({
  events,
  myBranch,
  title = "이벤트 & 프로모션",
  description = "내 지점과 촬영 일정에 맞는 혜택을 확인해보세요.",
  onClick,
  onViewAll,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const visibleEvents = useMemo(() => rankStoreEvents(
    Array.isArray(events) ? events.filter((event) => event.status !== "closed") : [],
    myBranch,
  ), [events, myBranch]);

  useEffect(() => setActiveIndex(0), [myBranch]);

  const handleTrackScroll = (event) => {
    const track = event.currentTarget;
    const firstCard = track.firstElementChild;
    const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth;
    const nextIndex = Math.round(track.scrollLeft / Math.max(1, cardWidth + 12));
    setActiveIndex(Math.max(0, Math.min(visibleEvents.length - 1, nextIndex)));
  };

  return (
    <section className="figma-home-store-event" data-node-id="6396:26981">
      <SectionTitle
        copy={description}
        actionLabel="전체보기"
        actionAriaLabel="이벤트 & 프로모션 전체보기"
        onAction={onViewAll}
      >
        {title}
      </SectionTitle>
      {visibleEvents.length > 0 ? (
        <>
          <div className="figma-home-store-event__track" onScroll={handleTrackScroll} aria-label="진행 중인 이벤트와 프로모션">
            {visibleEvents.map((event, index) => {
              const isApplied = Boolean(event.isApplied);
              return (
                <button
                  className="figma-home-store-card"
                  type="button"
                  onClick={() => onClick(event.id)}
                  key={event.id}
                  data-node-id={index === 0 ? "6396:26987" : undefined}
                >
                  <span className="figma-home-store-card__image" data-node-id={index === 0 ? "6396:27033" : undefined}>
                    <img src={event.image} alt={event.title} />
                    <em className={`figma-home-store-card__status is-${isApplied ? "applied" : event.status}`}>
                      {isApplied ? "신청 완료" : event.status === "ending" ? `진행중 · ${event.statusLabel}` : "진행중"}
                    </em>
                  </span>
                  <span className="figma-home-store-card__content" data-node-id={index === 0 ? "6396:26990" : undefined}>
                    <small className="figma-home-store-card__branch">{event.branch}</small>
                    <strong data-node-id={index === 0 ? "6396:26994" : undefined}>{event.title}</strong>
                    <p data-node-id={index === 0 ? "6396:27034" : undefined}>{event.description}</p>
                    <span className="figma-home-store-card__period" data-node-id={index === 0 ? "6396:26996" : undefined}>
                      <img src={ASSETS.storeEventCalendar} alt="" aria-hidden="true" data-node-id={index === 0 ? "6396:26997" : undefined} />
                      <small data-node-id={index === 0 ? "6396:26998" : undefined}>{event.period}</small>
                    </span>
                    <span className="figma-home-store-card__cta">{isApplied ? "신청 내역 보기" : "혜택 보기"}<i aria-hidden="true">›</i></span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="figma-home-store-event__pagination" aria-label={`이벤트 ${activeIndex + 1}/${visibleEvents.length}`}>
            {visibleEvents.map((event, index) => <i key={event.id} className={activeIndex === index ? "is-active" : ""} />)}
            <span>{activeIndex + 1} / {visibleEvents.length}</span>
          </div>
        </>
      ) : (
        <div className="figma-home-store-event__empty" role="status">
          <strong>진행 중인 이벤트가 없어요.</strong>
          <p>새로운 이벤트가 등록되면 이곳에서 안내해드릴게요.</p>
        </div>
      )}
    </section>
  );
}

function StudioReviewGallerySection({
  sections,
  title = "스튜디오 갤러리",
  description = "실제 고객님들이 남겨주신 소중한 순간들을 만나보세요",
  onPhotoClick,
  onViewAll,
}) {
  const [selectedFilterId, setSelectedFilterId] = useState("");
  const galleryFilters = useMemo(() => {
    const categoryFilters = (Array.isArray(sections) ? sections : []).map((section) => ({
      id: String(section.id || ""),
      name: section.name || "스튜디오 갤러리",
      label: formatGalleryCategoryLabel(section.name),
    }));
    return [
      { id: "", name: title, label: "전체" },
      ...categoryFilters,
    ];
  }, [sections, title]);
  const selectedSection = useMemo(
    () => (Array.isArray(sections) ? sections : []).find(
      (section) => String(section.id || "") === selectedFilterId,
    ),
    [sections, selectedFilterId],
  );
  const visiblePhotos = useMemo(() => {
    const sourcePosts = selectedSection
      ? selectedSection.items
      : (Array.isArray(sections) ? sections : []).flatMap((section) => section.items || []);
    return sourcePosts.map(mapGalleryPostCard).filter(Boolean).slice(0, 6);
  }, [sections, selectedSection]);
  const selectedFilter = galleryFilters.find((filter) => filter.id === selectedFilterId)
    || galleryFilters[0];

  return (
    <section className="figma-home-gallery" data-node-id="6396:27036">
      <div className="figma-home-gallery__header" data-node-id="6396:27037">
        <div className="figma-home-gallery__title-wrap" data-node-id="6396:27038">
          <div className="figma-home-gallery__title-row" data-node-id="6660:12662">
            <h2 data-node-id="6396:27042">{title}</h2>
            <button
              className="figma-home-gallery__view-all"
              type="button"
              onClick={() => onViewAll?.({
                id: selectedFilter?.id || "",
                name: selectedFilter?.name || title,
              })}
              data-node-id="6660:12657"
            >
              <span data-node-id="6660:12658">전체보기</span>
              <img src={ASSETS.galleryChevron6396} alt="" aria-hidden="true" data-node-id="6660:12659" />
            </button>
          </div>
        </div>
        <p className="figma-home-gallery__subtitle" data-node-id="6396:27044">{description}</p>
      </div>
      <div className="figma-home-gallery__filters" aria-label="스튜디오 갤러리 촬영분류 필터" data-node-id="6396:27083">
        {galleryFilters.map((filter) => (
          <button
            aria-pressed={selectedFilterId === filter.id}
            className={selectedFilterId === filter.id ? "is-selected" : ""}
            key={filter.id || "all"}
            type="button"
            onClick={() => setSelectedFilterId(filter.id)}
            data-node-id={selectedFilterId === filter.id ? "6396:27088" : undefined}
          >
            {filter.label}
          </button>
        ))}
      </div>
      <div className="figma-home-gallery__grid" data-node-id="6859:2038">
        {visiblePhotos.length === 0 ? (
          <p className="figma-home-gallery__empty">등록된 스튜디오 갤러리 사진이 없어요.</p>
        ) : null}
        {visiblePhotos.map((photo) => {
          const reviewPost = buildReviewPost(photo);

          return (
            <button
              aria-label={`${reviewPost.title} 갤러리 보기`}
              className={photo.image ? "has-image" : undefined}
              key={photo.id}
              type="button"
              onClick={() => onPhotoClick?.(photo)}
            >
              <span className="figma-home-gallery__image" aria-hidden={!photo.image}>
                <GalleryImage src={photo.image} alt={photo.title} />
              </span>
              <span className="figma-home-gallery__meta">
                <em>{formatStudioBranchLabel(photo.branch)}</em>
                <strong>{photo.title}</strong>
                <time>{photo.date || "등록일 미정"}</time>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function PopularThemeCard({ theme, featured, onClick }) {
  return (
    <button className={featured ? "figma-home-popular-card is-featured" : "figma-home-popular-card"} type="button" onClick={() => onClick?.(theme)} data-node-id={featured ? "6396:27287" : undefined}>
      <ErpImage src={theme.image} alt={`${theme.title} 테마 이미지`} />
      <span className="figma-home-popular-card__copy">
        <em>{theme.rank}위</em>
        <strong>{theme.title}</strong>
      </span>
    </button>
  );
}

function PopularThemeSection({
  themes,
  title = "인기 촬영 테마",
  description = "실제 고객님들이 남겨주신 소중한 순간들을 만나보세요",
  onThemeClick,
}) {
  return (
    <section className="figma-home-popular-theme" data-node-id="6396:27253">
      <SectionTitle copy={description}>{title}</SectionTitle>
      <div className="figma-home-popular-theme__grid">
        {themes.slice(0, 1).map((theme) => <PopularThemeCard key={theme.id || theme.rank} theme={theme} featured onClick={onThemeClick} />)}
        <div className="figma-home-popular-theme__row">
          {themes.slice(1, 3).map((theme) => <PopularThemeCard key={theme.id || theme.rank} theme={theme} onClick={onThemeClick} />)}
        </div>
      </div>
      <button className="figma-home-theme-link" type="button" onClick={() => onThemeClick?.()} data-node-id="7159:11057"><span>테마 구경하기</span><img src={ASSETS.themeChevron} alt="" aria-hidden="true" /></button>
    </section>
  );
}

function NoticeCenterPanel({ items = NOTICE_CENTER_ITEMS, onNavigate }) {
  return (
    <section className="figma-home-notice-center" aria-label="공지 문의 요약">
      <div className="figma-home-notice-center__nav">
        <div className="figma-home-notice-center__tabs" role="list">
          {NOTICE_CENTER_TABS.map((tab) => (
            <button
              key={tab.label}
              type="button"
              className={tab.active ? "figma-home-notice-center__tab is-active" : "figma-home-notice-center__tab"}
              onClick={() => onNavigate(tab.route)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button className="figma-home-notice-center__all" type="button" onClick={() => onNavigate("/faqList?tab=all")} aria-label="공지 문의 전체보기">
          <span>전체보기</span>
          <i aria-hidden="true" />
        </button>
      </div>
      <div className="figma-home-notice-center__list">
        {items.length > 0 ? (
          items.map((item, index) => (
            <button
              key={item.id || item.title}
              className={index === 0 ? "figma-home-notice-center__item is-featured" : "figma-home-notice-center__item"}
              type="button"
              onClick={() => onNavigate(item.route)}
            >
              <span className={`figma-home-notice-center__badge is-${item.type}`}>{item.badge}</span>
              <span className="figma-home-notice-center__body">
                <strong>{item.title}</strong>
                <small>{item.meta}</small>
              </span>
              <i className="figma-home-notice-center__chevron" aria-hidden="true" />
            </button>
          ))
        ) : (
          <div className="figma-home-notice-center__empty" role="status">
            <strong>등록된 공지가 없어요.</strong>
            <p>새로운 소식이 등록되면 이곳에서 안내해드릴게요.</p>
          </div>
        )}
      </div>
      <button className="figma-home-notice-center__cta" type="button" onClick={() => onNavigate("/qnaList/write")}>
        <img src={ASSETS.noticeChat} alt="" aria-hidden="true" />
        <span>문의 남기기</span>
      </button>
    </section>
  );
}

function ErpImage({ src, alt = "" }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <span className="figma-home-image-pending" role="img" aria-label="이미지 준비 중">
        <svg viewBox="0 0 48 48" aria-hidden="true">
          <rect x="7" y="10" width="34" height="28" rx="4" />
          <circle cx="17" cy="20" r="3" />
          <path d="m11 34 9-9 6 6 4-4 7 7" />
        </svg>
        <span>이미지 준비 중</span>
        <small>ERP에 이미지가 등록되면 자동으로 표시됩니다</small>
      </span>
    );
  }

  return <img src={src} alt={alt} onError={() => setFailed(true)} />;
}

function NoticeSection({
  noticeItems,
  title = "공지 · 문의",
  description = "중요한 소식과 안내를 한눈에 확인하세요",
  onNavigate,
}) {
  return (
    <section className="figma-home-notice" data-node-id="6396:27344">
      <div className="figma-home-notice__header" data-node-id="6396:27345">
        <div className="figma-home-notice__title-wrap" data-node-id="6396:27346">
          <h2 data-node-id="6396:27350">{title}</h2>
        </div>
        <p data-node-id="6396:27352">{description}</p>
      </div>
      <article className="figma-home-notice-hero" data-node-id="7123:21818">
        <img className="figma-home-notice-hero__bg" src={ASSETS.noticeHeroBg} alt="" aria-hidden="true" data-node-id="7123:21836" />
        <div className="figma-home-notice-hero__copy" data-node-id="7123:21819">
          <span className="figma-home-notice-hero__text" data-node-id="7123:21820">
            <strong data-node-id="7123:21825">스튜디오와 소통하기</strong>
            <p data-node-id="7123:21827">궁금한 점이나 요청사항이 있으신가요?<br />친절하게 도와드릴게요.</p>
          </span>
        </div>
      </article>
      <NoticeCenterPanel items={noticeItems} onNavigate={onNavigate} />
    </section>
  );
}

function HomeSubHeader({ title, onBack, actionLabel, onAction }) {
  return (
    <header className={`figma-home-sub-header${actionLabel ? " has-action" : ""}`}>
      <button type="button" onClick={onBack} aria-label="뒤로가기">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 5-7 7 7 7" />
        </svg>
      </button>
      <h1>{title}</h1>
      {actionLabel ? (
        <button className="figma-home-sub-header__action" type="button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : <span aria-hidden="true" />}
    </header>
  );
}

function PublicContentDetailPage({ item, onBack, onNavigate }) {
  const publishedDate = formatPublicContentDate(item?.publishedAt);

  return (
    <section className="figma-home-page mobile-app-frame" data-node-id="home-public-content-detail">
      <StatusBar />
      <HomeSubHeader title="공지사항" onBack={onBack} />
      <main className="figma-home-detail-scroll">
        <section className="figma-home-event-detail figma-home-public-content-detail">
          {item?.image ? (
            <div className="figma-home-event-detail__hero">
              <img src={item.image} alt="" aria-hidden="true" />
            </div>
          ) : null}
          <div className="figma-home-event-detail__content">
            <span>{item?.officeId ? "내 지점 공지" : "전체 지점 공지"}</span>
            <h2>{item?.title || "공지 내용을 불러올 수 없습니다."}</h2>
            {item?.summary ? <p>{item.summary}</p> : null}
            {publishedDate ? (
              <div className="figma-home-event-detail__period">
                <img src={ASSETS.storeEventCalendar} alt="" aria-hidden="true" />
                <small>{publishedDate}</small>
              </div>
            ) : null}
          </div>
          <p className="figma-home-event-notice figma-home-public-content-detail__body">
            {item?.content || "잠시 후 다시 확인해주세요."}
          </p>
          <div className="figma-home-event-detail__actions">
            <button type="button" onClick={onBack}>홈으로 돌아가기</button>
            <button type="button" onClick={() => onNavigate("/qnaList/write")}>문의하기</button>
          </div>
        </section>
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
    </section>
  );
}

function ReviewEventPhotoDetailPage({ photos = [], onBack, onNavigate }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [downloadState, setDownloadState] = useState("");
  const displayPhotos = Array.isArray(photos) ? photos : [];
  const selectedPhoto = displayPhotos[selectedIndex] || displayPhotos[0];

  if (!selectedPhoto) {
    return (
      <section className="figma-home-page mobile-app-frame">
        <StatusBar />
        <HomeSubHeader title="후기이벤트 사진" onBack={onBack} />
        <MystudioDataState type="empty" title="업로드된 사진이 없어요" />
        <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
      </section>
    );
  }

  const handleSelectedDownload = async () => {
    setDownloadState("선택한 사진 저장을 준비하고 있어요.");
    await downloadImageToLocal(selectedPhoto.image, selectedPhoto.title);
    setDownloadState("선택한 사진 저장을 시작했어요.");
  };

  const handleAllDownload = async () => {
    setDownloadState(`후기이벤트 보정사진 ${displayPhotos.length}컷 저장을 시작했어요.`);

    for (const photo of displayPhotos) {
      await downloadImageToLocal(photo.image, photo.title);
    }
  };

  return (
    <section className="figma-home-page mobile-app-frame figma-home-review-event-page" data-node-id="home-review-event-photo-detail">
      <StatusBar />
      <HomeSubHeader title="후기이벤트 사진" onBack={onBack} />
      <main className="figma-home-detail-scroll">
        <section className="figma-home-review-event-viewer">
          <div className="figma-home-review-event-viewer__hero">
            <img src={selectedPhoto.image} alt={selectedPhoto.title} />
            <span>{`${selectedIndex + 1}/${displayPhotos.length}`}</span>
          </div>
          <div className="figma-home-review-event-viewer__copy">
            <span>스튜디오 업로드 보정사진</span>
            <h2>{selectedPhoto.title}</h2>
            <p>{selectedPhoto.description || "스튜디오에서 업로드한 후기이벤트 보정사진을 확인하고 저장할 수 있어요."}</p>
            {selectedPhoto.date ? <time>{selectedPhoto.date}</time> : null}
          </div>
          <div className="figma-home-review-event-viewer__thumbs" aria-label="후기이벤트 사진 선택">
            {displayPhotos.map((photo, index) => (
              <button
                key={photo.title}
                className={selectedIndex === index ? "is-selected" : ""}
                type="button"
                onClick={() => setSelectedIndex(index)}
                aria-label={`${index + 1}번째 후기이벤트 사진 보기`}
              >
                <img src={photo.image} alt="" aria-hidden="true" />
                <span>{index + 1}</span>
              </button>
            ))}
          </div>
          <div className="figma-home-review-event-viewer__actions">
            <button className="figma-home-review-event-viewer__primary" type="button" onClick={handleSelectedDownload}>
              선택 사진 저장하기
            </button>
            <button type="button" onClick={handleAllDownload}>
              전체 저장하기
            </button>
          </div>
          {downloadState ? <p className="figma-home-review-event-viewer__status">{downloadState}</p> : null}
        </section>
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
    </section>
  );
}

function GalleryPostListPage({
  categoryId,
  categoryName,
  onBack,
  onNavigate,
  onPostClick,
}) {
  const [sort, setSort] = useState("latest");
  const galleryQuery = useMystudioGalleryPostsV4({
    page: 1,
    limit: 50,
    categoryId,
    sort,
  });
  const posts = galleryQuery.data?.data?.items || [];

  return (
    <section className="figma-home-page mobile-app-frame figma-home-gallery-list-page">
      <StatusBar />
      <HomeSubHeader title={categoryName || "스튜디오 갤러리"} onBack={onBack} />
      <main className="figma-home-detail-scroll">
        <header className="figma-home-gallery-list__header">
          <div>
            <h2>{categoryName || "스튜디오 갤러리"}</h2>
            <p>스튜디오에서 등록한 촬영 이야기를 만나보세요.</p>
          </div>
          <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="게시물 정렬">
            <option value="latest">최신순</option>
            <option value="popular">인기순</option>
          </select>
        </header>
        {galleryQuery.isLoading ? (
          <MystudioDataState type="loading" title="갤러리 게시물을 불러오고 있어요." />
        ) : null}
        {galleryQuery.isError ? (
          <MystudioDataState type="error" title="갤러리를 불러오지 못했어요." onRetry={() => galleryQuery.refetch?.()} />
        ) : null}
        {!galleryQuery.isLoading && !galleryQuery.isError && posts.length === 0 ? (
          <MystudioDataState type="empty" title="등록된 갤러리 게시물이 없어요." />
        ) : null}
        <div className="figma-home-gallery-list__grid">
          {posts.map(mapGalleryPostCard).filter(Boolean).map((post) => (
            <button key={post.id} type="button" onClick={() => onPostClick?.(post)}>
              <span className="figma-home-gallery-list__thumb">
                <GalleryImage src={post.image} alt={post.title} />
              </span>
              <strong>{post.title}</strong>
              <span>{post.author}</span>
              <small>{post.date} · 조회 {post.viewCount}</small>
            </button>
          ))}
        </div>
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
    </section>
  );
}

function ReviewPhotoDetailPage({ photo, onBack, onNavigate }) {
  const galleryPostQuery = useMystudioGalleryPostV4(photo?.galleryPostId || photo?.id);
  const viewMutation = useRegisterMystudioGalleryViewV4();
  const viewedPostRef = useRef("");
  const livePost = galleryPostQuery.data?.data;
  const resolvedPhoto = livePost ? {
    id: livePost.id,
    galleryPostId: livePost.id,
    title: livePost.title,
    branch: livePost.officeName,
    category: livePost.category?.name,
    author: livePost.authorDisplayName,
    description: livePost.summary,
    viewCount: livePost.viewCount,
    date: formatReviewDate(livePost.createdAt),
    photoCount: livePost.photoCount,
    image: livePost.photos?.[0]?.thumbnailUrl || livePost.photos?.[0]?.imageUrl || "",
    images: (livePost.photos || []).map((image) => ({
      src: image.imageUrl || image.thumbnailUrl || "",
      title: image.altText || livePost.title,
      caption: image.altText || livePost.category?.name || "",
    })),
  } : photo;
  const reviewPost = buildReviewPost(resolvedPhoto);

  useEffect(() => {
    const postId = String(photo?.galleryPostId || photo?.id || "");
    if (!postId || viewedPostRef.current === postId) return;
    viewedPostRef.current = postId;
    viewMutation.mutate(postId);
  }, [photo?.galleryPostId, photo?.id]);

  if (!reviewPost) {
    return (
      <section className="figma-home-page mobile-app-frame">
        <StatusBar />
        <HomeSubHeader title="스튜디오 갤러리" onBack={onBack} />
        <MystudioDataState type="empty" title="선택한 갤러리 사진을 찾을 수 없어요" />
        <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
      </section>
    );
  }

  return (
    <section className="figma-home-page mobile-app-frame figma-home-review-post-page" data-node-id="home-review-photo-detail">
      <StatusBar />
      <HomeSubHeader title="스튜디오 갤러리" onBack={onBack} />
      <main className="figma-home-detail-scroll">
        {galleryPostQuery.isLoading ? (
          <MystudioDataState type="loading" title="스튜디오 갤러리 사진을 불러오고 있어요." />
        ) : null}
        {galleryPostQuery.isError ? (
          <MystudioDataState type="error" title="사진을 불러오지 못했어요." onRetry={() => galleryPostQuery.refetch?.()} />
        ) : null}
        <article className="figma-home-review-post">
          <section className="figma-home-review-post__intro">
            <span>{reviewPost.branch}</span>
            <h2>{reviewPost.title}</h2>
            <p>{reviewPost.theme}</p>
            <p>{resolvedPhoto?.description}</p>
            <small>{resolvedPhoto?.author || "MyStudio"} · {resolvedPhoto?.date || ""} · 조회 {resolvedPhoto?.viewCount || 0}</small>
            <small>사진 {reviewPost.images.length}장</small>
          </section>

          <section className="figma-home-review-post__photos" aria-label="스튜디오 갤러리 사진 모음">
            {!galleryPostQuery.isLoading && reviewPost.images.length === 0 ? (
              <GalleryImage src="" alt={reviewPost.title} />
            ) : null}
            {reviewPost.images.map((image, index) => (
              <figure key={`${image.src}-${index}`}>
                <div className="figma-home-review-post__photo">
                  <GalleryImage src={image.src} alt={image.title} />
                  <span>{`${index + 1}/${reviewPost.images.length}`}</span>
                </div>
                <figcaption>
                  <span>{`${index + 1} / ${reviewPost.images.length}`}</span>
                </figcaption>
              </figure>
            ))}
          </section>
        </article>
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
    </section>
  );
}

function getStoreEventBadge(event) {
  if (event?.status === "closed") return { label: "마감", tone: "closed" };
  if (event?.status === "ending") {
    return { label: `진행중 · ${event.statusLabel || ""}`.trim(), tone: "ending" };
  }
  return { label: "진행중", tone: "open" };
}

function EventHubCard({ event, onClick }) {
  const badge = getStoreEventBadge(event);
  const branchLabel = normalizeEventBranch(event.branchName || event.branch) || "전 지점";
  const ctaLabel = event.status === "closed"
    ? "지난 이벤트 보기"
    : event.isApplied ? "신청 완료" : "혜택 보기";

  return (
    <button
      className={`figma-home-event-hub-card${event.status === "closed" ? " is-closed" : ""}`}
      type="button"
      onClick={() => onClick(event.id)}
    >
      <span className="figma-home-event-hub-card__image">
        <img src={event.image} alt="" aria-hidden="true" />
        <em className={`is-${badge.tone}`}>{badge.label}</em>
      </span>
      <span className="figma-home-event-hub-card__body">
        <strong>{event.benefitTitle || event.title}</strong>
        <span className="figma-home-event-hub-card__period">
          <small>{branchLabel}</small>
          <i aria-hidden="true">·</i>
          <small>{event.shortPeriod || event.period}</small>
        </span>
        <span className="figma-home-event-hub-card__cta">{ctaLabel}<i aria-hidden="true">›</i></span>
      </span>
    </button>
  );
}

function EventPromotionHubPage({
  events,
  initialStatus,
  onBack,
  onEventClick,
  onNavigate,
  onStatusChange,
}) {
  const resolvedInitialStatus = normalizeEventStatus(initialStatus);
  const [selectedStatus, setSelectedStatus] = useState(resolvedInitialStatus);
  const statusTabRefs = useRef({});
  const ongoingEvents = useMemo(() => sortOngoingEvents(
    filterEventsByStatus(events, "ongoing"),
  ), [events]);
  const closedEvents = useMemo(() => filterEventsByStatus(events, "closed"), [events]);
  const allEvents = useMemo(() => [...ongoingEvents, ...closedEvents], [closedEvents, ongoingEvents]);
  const eventsByStatus = selectedStatus === "all"
    ? allEvents
    : selectedStatus === "closed" ? closedEvents : ongoingEvents;

  const selectStatus = (status) => {
    setSelectedStatus(status);
    onStatusChange?.(status);
  };

  const handleStatusTabKeyDown = (event, currentIndex) => {
    const lastIndex = EVENT_STATUS_TABS.length - 1;
    let nextIndex = currentIndex;

    if (event.key === "ArrowRight") nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    else if (event.key === "ArrowLeft") nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = lastIndex;
    else return;

    event.preventDefault();
    const nextStatus = EVENT_STATUS_TABS[nextIndex].key;
    selectStatus(nextStatus);
    window.requestAnimationFrame(() => statusTabRefs.current[nextStatus]?.focus());
  };

  useEffect(() => {
    setSelectedStatus(resolvedInitialStatus);
  }, [resolvedInitialStatus]);

  return (
    <section className="figma-home-page mobile-app-frame figma-home-event-hub-page" data-node-id="home-event-promotion-hub">
      <StatusBar />
      <HomeSubHeader title="이벤트 & 프로모션" onBack={onBack} />
      <main className="figma-home-event-hub-scroll">
        <div className="figma-home-event-hub figma-home-event-hub--simple">
          <div className="figma-home-event-hub__tabs" role="tablist" aria-label="이벤트 상태">
            {EVENT_STATUS_TABS.map((tab, index) => {
              const count = tab.key === "all"
                ? allEvents.length
                : tab.key === "closed" ? closedEvents.length : ongoingEvents.length;
              const isSelected = selectedStatus === tab.key;
              return (
                <button
                  className={isSelected ? "is-active" : ""}
                  id={`event-status-tab-${tab.key}`}
                  type="button"
                  role="tab"
                  ref={(node) => { statusTabRefs.current[tab.key] = node; }}
                  aria-selected={isSelected}
                  aria-controls="event-status-panel"
                  tabIndex={isSelected ? 0 : -1}
                  key={tab.key}
                  onClick={() => selectStatus(tab.key)}
                  onKeyDown={(event) => handleStatusTabKeyDown(event, index)}
                >
                  <span>{tab.label}</span>
                  <em>{count}</em>
                </button>
              );
            })}
          </div>

          <section
            className="figma-home-event-hub__status-panel"
            id="event-status-panel"
            role="tabpanel"
            aria-labelledby={`event-status-tab-${selectedStatus}`}
          >
            {eventsByStatus.length > 0 ? (
              <div className="figma-home-event-hub__card-list">
                {eventsByStatus.map((event) => (
                  <EventHubCard event={event} onClick={onEventClick} key={event.id} />
                ))}
              </div>
            ) : (
              <div className="figma-home-event-hub__empty">
                <strong>{selectedStatus === "all" ? "아직 등록된 이벤트가 없어요." : selectedStatus === "closed" ? "아직 마감된 이벤트가 없어요." : "현재 진행 중인 이벤트가 없어요."}</strong>
                <p>{selectedStatus === "all" ? "새로운 이벤트가 시작되면 바로 안내해드릴게요." : selectedStatus === "closed" ? "마감된 이벤트가 생기면 이곳에서 확인할 수 있어요." : "새로운 이벤트가 시작되면 바로 안내해드릴게요."}</p>
              </div>
            )}
          </section>
        </div>
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
    </section>
  );
}
function StoreEventHomePage({
  event,
  isEventApplied,
  isApplying,
  applyError,
  onApply,
  onBack,
  onViewEvents,
  onNavigate,
}) {
  const activeEvent = event;

  if (!activeEvent) {
    return (
      <section className="figma-home-page mobile-app-frame" data-node-id="home-store-event-detail">
        <StatusBar />
        <HomeSubHeader title="이벤트 & 프로모션" onBack={onBack} />
        <main className="figma-home-detail-scroll">
          <div className="figma-home-event-hub__empty" role="status">
            <strong>현재 확인할 수 있는 이벤트가 없어요.</strong>
            <p>새로운 이벤트가 등록되면 이곳에서 안내해드릴게요.</p>
            <button type="button" onClick={onViewEvents}>이벤트 목록 보기</button>
          </div>
        </main>
        <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
      </section>
    );
  }

  const isClosed = activeEvent.status === "closed";
  const isReadOnly = Boolean(activeEvent.readOnly);
  const benefits = Array.isArray(activeEvent.benefits) ? activeEvent.benefits : [];
  const ctaAction = activeEvent.cta?.action || "";
  const ctaPayload = activeEvent.cta?.payload || {};
  const externalUrl = ctaAction === "EXTERNAL_URL"
    ? getSafeHttpsDownloadUrl(ctaPayload.url)
    : "";
  const appRoute = ctaAction === "APP_ROUTE"
    ? getSafeInternalAppPath(ctaPayload.route)
    : "";

  return (
    <section className="figma-home-page mobile-app-frame" data-node-id="home-store-event-detail">
      <StatusBar />
      <HomeSubHeader title="이벤트 & 프로모션" onBack={onBack} />
      <main className="figma-home-detail-scroll">
        <section className="figma-home-event-detail">
          <div className="figma-home-event-detail__hero">
            {activeEvent.image ? (
              <img src={activeEvent.image} alt={activeEvent.title} />
            ) : (
              <span className="figma-home-event-detail__image-label">이벤트 이미지 준비 중</span>
            )}
          </div>
          <div className="figma-home-event-detail__content">
            <span>{activeEvent.branch}</span>
            <h2>{activeEvent.title}</h2>
            <p>{activeEvent.description}</p>
            <div className="figma-home-event-detail__period">
              <img src={ASSETS.storeEventCalendar} alt="" aria-hidden="true" />
              <small>{activeEvent.period}</small>
            </div>
          </div>
          {benefits.length > 0 ? (
            <div className="figma-home-event-benefits">
              <h3>이벤트 혜택</h3>
              {benefits.map((benefit) => (
                <div className="figma-home-event-benefit" key={benefit}>
                  <span aria-hidden="true">✓</span>
                  <strong>{benefit}</strong>
                </div>
              ))}
            </div>
          ) : null}
          <p className="figma-home-event-notice">{activeEvent.notice}</p>
          {!isReadOnly && isEventApplied ? (
            <div className="figma-home-event-apply-complete" role="status">
              <strong>이벤트 신청이 완료되었습니다.</strong>
              <p>신청 내역은 지점 확인 후 알림으로 안내드릴게요.</p>
              <button type="button" onClick={onViewEvents}>이벤트 목록 보기</button>
            </div>
          ) : null}
          {applyError ? <p className="figma-home-event-apply-error" role="alert">{applyError}</p> : null}
          <div className="figma-home-event-detail__actions">
            {externalUrl ? (
              <a
                className="figma-home-event-detail__primary-link"
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {activeEvent.cta?.label || "공식 페이지 보기"}
              </a>
            ) : appRoute ? (
              <button type="button" onClick={() => onNavigate(appRoute)}>
                {activeEvent.cta?.label || "자세히 보기"}
              </button>
            ) : isReadOnly ? (
              <button type="button" onClick={onViewEvents}>이벤트 목록 보기</button>
            ) : (
              <button type="button" onClick={() => onApply(activeEvent.id)} disabled={isEventApplied || isClosed || isApplying}>
                {isClosed ? "마감된 이벤트" : isEventApplied ? "신청 완료" : isApplying ? "신청 중" : activeEvent.cta?.label || "이벤트 신청하기"}
              </button>
            )}
            <button type="button" onClick={() => onNavigate("/qnaList/write")}>이벤트 문의하기</button>
          </div>
        </section>
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={onNavigate} />
    </section>
  );
}

export default function HomePage({ onNavigate } = {}) {
  const routerNavigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [snoozeRevision, setSnoozeRevision] = useState(0);
  const [homeRefreshToken, setHomeRefreshToken] = useState(0);
  const [appliedStoreEventIds, setAppliedStoreEventIds] = useState([]);
  const [eventApplyError, setEventApplyError] = useState("");
  const homeQuery = useMystudioHomeV4();
  const eventApplyMutation = useApplyPublicEventV4();
  const homeAvailability = useMemo(() => resolveHomeAvailability(homeQuery), [
    homeQuery.data, homeQuery.error, homeQuery.configured, homeQuery.isLoading,
  ]);
  const v4Home = homeAvailability.home;
  const isPublicDemoSession = isPublicPasswordlessDemoSession({ token: getAccountData.token() });
  const { pageConfig: homePageConfig } = useAppPageConfig(APP_PAGE_KEYS.home);
  const actionNow = useMemo(() => new Date(), [homeRefreshToken]);
  const customerId = Number(v4Home?.profile?.customerId) || 0;
  const actionSnoozeStorage = customerId > 0 ? window.localStorage : window.sessionStorage;
  const v4ProductActionRows = useMemo(() => (v4Home?.products || []).map((product) => ({
    contract_product_id: product.id,
    product_name: product.title,
    status_cd: ["not_started", "draft"].includes(product.status) ? "2" : product.status,
    required_photo_count: product.requiredPhotoCount || 0,
    selected_photo_count: product.selectedPhotoCount || 0,
    product_due_at: product.dueAt || "",
  })), [v4Home?.products]);
  const v4PhotoActionRows = useMemo(() => (v4Home?.completedSchedules || [])
    .filter((scheduleRow) => scheduleRow.originalPhotos?.status === "available")
    .map((scheduleRow) => ({
      schedule_id: scheduleRow.id,
      new_download_file_count: scheduleRow.originalPhotos?.count || 0,
      download_expires_at: scheduleRow.originalPhotos?.expiresAt || "",
      joined_event: v4Home?.reviewEvent?.status && v4Home.reviewEvent.status !== "not_applied" ? "Y" : "N",
    })), [v4Home?.completedSchedules, v4Home?.reviewEvent?.status]);
  const effectiveScheduleRows = v4Home ? v4Home.upcomingSchedules : [];
  const effectiveCompletedScheduleRows = v4Home ? v4Home.completedSchedules : [];
  const effectiveProductRows = v4Home ? v4ProductActionRows : [];
  const effectiveReviewApplicationRows = v4Home ? v4PhotoActionRows : [];
  const effectiveReviewPhotoRows = v4Home ? v4Home.reviewPhotos : [];
  const effectiveGalleryPostRows = v4Home ? v4Home.galleryPosts : [];
  const effectiveGallerySections = v4Home ? v4Home.gallerySections : [];
  const effectiveHomeDataLoading = homeQuery.isLoading;
  const effectiveActionDataStatus = homeAvailability.liveHome
    ? homeQuery.error ? "partial" : "success"
    : homeAvailability.loading ? "loading" : "error";
  const userName = v4Home?.profile?.displayName || "";

  const navigate = (target) => {
    const nextTarget = withMobileView(target);

    if (onNavigate) {
      onNavigate(nextTarget);
      return;
    }
    routerNavigate(nextTarget);
  };

  const replaceNavigate = (target) => {
    const nextTarget = withMobileView(target);
    routerNavigate(nextTarget, { replace: true });
  };

  const navigateBack = (fallbackTarget) => {
    const historyIndex = Number(window.history.state?.idx);
    if (Number.isInteger(historyIndex) && historyIndex > 0) {
      routerNavigate(-1);
      return;
    }
    replaceNavigate(fallbackTarget);
  };

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        setHomeRefreshToken((value) => value + 1);
      }
    };
    const refreshTimer = window.setInterval(refreshWhenVisible, 5 * 60 * 1000);

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(refreshTimer);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  const actionItems = useMemo(() => buildCustomerActionItems({
    schedules: effectiveScheduleRows,
    products: effectiveProductRows,
    photos: effectiveReviewApplicationRows,
    now: actionNow,
    fallbackToPreview: false,
  }), [
    actionNow,
    effectiveProductRows,
    effectiveReviewApplicationRows,
    effectiveScheduleRows,
  ]);

  const isShowingPreviewActions = actionItems.some((item) => item.sourceKind === "preview");

  const visibleActionItems = useMemo(() => filterSnoozedActionItems(filterCompletedCustomerActionItems(actionItems, {
    customerId,
    storage: actionSnoozeStorage,
  }), {
    customerId,
    now: actionNow,
    storage: actionSnoozeStorage,
  }).map((item) => ({
    ...item,
    canSnooze: !(item.type === CUSTOMER_ACTION_TYPES.ORIGINAL_DOWNLOAD && item.remainingDays !== null && item.remainingDays <= 1),
  })), [actionItems, actionNow, actionSnoozeStorage, customerId, snoozeRevision]);
  const areTodayTasksLoading = effectiveActionDataStatus === "loading" && visibleActionItems.length === 0;

  useEffect(() => {
    const count = cacheCustomerActionCount({
      customerId,
      count: visibleActionItems.length,
      storage: window.sessionStorage,
    });
    window.dispatchEvent(new CustomEvent(CUSTOMER_ACTION_COUNT_EVENT, {
      detail: { customerId, count },
    }));
  }, [customerId, visibleActionItems.length]);

  const schedule = useMemo(() => mapSchedule(effectiveScheduleRows[0]), [effectiveScheduleRows]);
  const myBranch = schedule ? normalizeEventBranch(schedule.studio) : "";
  const recentShooting = useMemo(
    () => mapRecentShooting(effectiveCompletedScheduleRows[0]),
    [effectiveCompletedScheduleRows],
  );
  const reviewEventPhotos = useMemo(() => {
    if (effectiveReviewPhotoRows.length > 0) {
      return effectiveReviewPhotoRows.map((row, index) => ({
        ...mapReviewPhoto(row, index),
        description: "스튜디오에서 업로드한 후기 이벤트 보정사진입니다.",
      })).filter((photo) => photo?.image);
    }

    return [];
  }, [effectiveReviewPhotoRows]);
  const reviewEventStatus = v4Home?.reviewEvent
    ? v4Home.reviewEvent.availability === "unavailable"
      ? REVIEW_EVENT_STATUS.unavailable
      : v4Home.reviewEvent.status === "uploaded"
      ? REVIEW_EVENT_STATUS.ready
      : v4Home.reviewEvent.status === "not_applied"
        ? REVIEW_EVENT_STATUS.notApplied
        : REVIEW_EVENT_STATUS.processing
    : resolveReviewEventStatus({
        loading: effectiveHomeDataLoading,
        applicationRows: effectiveReviewApplicationRows,
        uploadedRows: effectiveReviewPhotoRows,
        previewState: null,
      });
  const effectivePublicContentItems = v4Home
    ? [...(v4Home.events || []), ...(v4Home.notices || []), ...(v4Home.faqs || [])]
    : [];
  const mappedPublicContent = useMemo(() => mapPublicContentItems(effectivePublicContentItems, {
    eventFallbackImage: "",
    noticeFallbackImage: "",
  }), [effectivePublicContentItems]);
  const resolvedPublicContent = useMemo(() => resolvePublicContentSources(
    mappedPublicContent,
    {
      isAuthoritative: Boolean(v4Home),
      fallbackEvents: [],
      fallbackNotices: [],
    },
  ), [mappedPublicContent, v4Home]);
  const eventSource = resolvedPublicContent.events;
  const inquiryCenterItems = (v4Home?.communication?.recentInquiries || []).map((inquiry) => ({
    id: `inquiry-${inquiry.id}`,
    type: "inquiry",
    badge: inquiry.unreadReplyCount > 0 ? "새 답변" : "문의",
    title: inquiry.title,
    meta: inquiry.answer ? "스튜디오 답변이 등록되었습니다" : "답변을 기다리고 있습니다",
    route: `/qnaList/${encodeURIComponent(inquiry.id)}?screen=inquiry-detail`,
  }));
  const noticeCenterItems = [...inquiryCenterItems, ...resolvedPublicContent.notices].slice(0, 5);
  const storeEvents = useMemo(() => eventSource.map((event) => ({
    ...event,
    isApplied: event.readOnly ? false : appliedStoreEventIds.includes(event.id),
  })), [appliedStoreEventIds, eventSource]);
  const selectedStoreEvent = useMemo(() => {
    const eventId = searchParams.get("eventId");
    return storeEvents.find((event) => event.id === eventId) || storeEvents[0];
  }, [searchParams, storeEvents]);
  const selectedPublicContent = useMemo(() => {
    const contentId = searchParams.get("contentId");
    return mappedPublicContent.notices.find((item) => item.id === contentId) || null;
  }, [mappedPublicContent.notices, searchParams]);
  const gallerySections = useMemo(() => {
    if (effectiveGallerySections.length > 0) return effectiveGallerySections;
    if (effectiveGalleryPostRows.length === 0) return [];
    return [{
      id: effectiveGalleryPostRows[0]?.category?.id || "",
      name: effectiveGalleryPostRows[0]?.category?.name || "스튜디오 갤러리",
      description: "스튜디오에서 등록한 촬영 이야기를 만나보세요.",
      sortOrder: 0,
      items: effectiveGalleryPostRows,
      total: effectiveGalleryPostRows.length,
    }];
  }, [effectiveGalleryPostRows, effectiveGallerySections]);
  const popularThemes = useMemo(() => {
    return (v4Home?.popularThemes || []).map((theme, index) => ({
      id: theme.id,
      rank: theme.rank || index + 1,
      title: theme.title,
      image: theme.thumbnailUrl || theme.images?.[0]?.url || "",
    }));
  }, [v4Home]);
  const visibleHomeSections = useMemo(
    () => getVisibleSections(homePageConfig).filter(
      (section) => section.key !== "review_event",
    ),
    [homePageConfig],
  );
  const activeScreen = searchParams.get("screen");
  const isTodayTasksScreen = activeScreen === "today-tasks";
  const isMobileNotificationScreen = activeScreen === "notifications";
  const isReviewEventPhotoScreen = activeScreen === "review-event-photo";
  const isMobileReviewPhotoScreen = activeScreen === "review-photo" || activeScreen === "studio-review-detail";
  const isGalleryListScreen = activeScreen === "gallery-list";
  const isEventPromotionHubScreen = activeScreen === "events";
  const isMobileStoreEventScreen = activeScreen === "store-event";
  const isPublicContentScreen = activeScreen === "public-content";

  const createTodayTasksPath = ({ filterKey, actionId } = {}) => {
    const nextParams = new URLSearchParams({ screen: "today-tasks" });
    if (filterKey) nextParams.set("type", filterKey);
    if (actionId) nextParams.set("actionId", actionId);
    if (searchParams.get("actionPreview") === "1") nextParams.set("actionPreview", "1");
    return `/tabs/home?${nextParams.toString()}`;
  };

  const openReviewEventPhoto = () => {
    const previewState = searchParams.get("reviewEventState");
    navigate(`/tabs/home?screen=review-event-photo${previewState ? `&reviewEventState=${encodeURIComponent(previewState)}` : ""}`);
  };

  const openReviewPhoto = (photo) => {
    saveReviewPhotoSelection(photo);
    const nextParams = new URLSearchParams({ screen: "review-photo" });
    if (photo?.galleryPostId || photo?.id) {
      nextParams.set("galleryPostId", photo.galleryPostId || photo.id);
    }
    navigate(`/tabs/home?${nextParams.toString()}`);
  };

  const openGalleryList = (section) => {
    const nextParams = new URLSearchParams({
      screen: "gallery-list",
      categoryId: section?.id || "",
      categoryName: section?.name || "스튜디오 갤러리",
    });
    navigate(`/tabs/home?${nextParams.toString()}`);
  };

  const openStoreEvent = (eventId) => {
    navigate(`/tabs/home?screen=store-event&eventId=${encodeURIComponent(eventId)}`);
  };

  const openStoreEventFromHub = (eventId) => {
    const nextParams = new URLSearchParams({
      screen: "store-event",
      eventId,
      from: "events",
    });
    const returnStatus = normalizeEventStatus(searchParams.get("status"));
    if (returnStatus !== "all") nextParams.set("returnStatus", returnStatus);
    navigate(`/tabs/home?${nextParams.toString()}`);
  };

  const openEventPromotionHub = () => {
    navigate("/tabs/home?screen=events");
  };

  const returnToEventList = () => {
    const historyIndex = Number(window.history.state?.idx);
    if (searchParams.get("from") === "events" && Number.isInteger(historyIndex) && historyIndex > 0) {
      routerNavigate(-1);
      return;
    }
    routerNavigate(withMobileView(getEventHubReturnPath()), { replace: true });
  };

  const updateEventHubStatus = (status) => {
    const nextParams = new URLSearchParams({ screen: "events" });
    const normalizedStatus = normalizeEventStatus(status);
    if (normalizedStatus !== "all") nextParams.set("status", normalizedStatus);
    replaceNavigate(`/tabs/home?${nextParams.toString()}`);
  };

  const getEventHubReturnPath = () => {
    const nextParams = new URLSearchParams({ screen: "events" });
    const returnStatus = normalizeEventStatus(searchParams.get("returnStatus"));
    if (returnStatus !== "all") nextParams.set("status", returnStatus);
    return `/tabs/home?${nextParams.toString()}`;
  };

  const applyStoreEvent = async (eventId) => {
    const event = storeEvents.find((item) => item.id === eventId);
    if (!event || event.readOnly) return;

    setEventApplyError("");
    try {
      await eventApplyMutation.mutateAsync({ contentId: String(event.sourceId) });
      setAppliedStoreEventIds((currentIds) => {
        const nextIds = currentIds.includes(eventId) ? currentIds : [...currentIds, eventId];
        return nextIds;
      });
    } catch (error) {
      setEventApplyError(getMystudioErrorMessage(error));
    }
  };

  const openCustomerAction = (item) => {
    if (item?.cta?.path) navigate(item.cta.path);
  };

  const snoozeCustomerAction = (item) => {
    if (!item?.id) return;
    const saved = snoozeActionItem({ customerId, actionId: item.id, now: new Date(), storage: actionSnoozeStorage });
    if (saved) setSnoozeRevision((value) => value + 1);
  };

  const retryCustomerActions = () => {
    setHomeRefreshToken((value) => value + 1);
    if (homeQuery.configured) homeQuery.refetch();
  };

  const closeHomeSubScreen = () => {
    navigate("/tabs/home");
  };

  if (isTodayTasksScreen) {
    return (
      <TodayTasksPage
        items={visibleActionItems}
        filterKey={searchParams.get("type")}
        actionId={searchParams.get("actionId")}
        loading={areTodayTasksLoading}
        dataStatus={effectiveActionDataStatus}
        isPreview={isShowingPreviewActions}
        onBack={() => navigateBack("/tabs/home")}
        onShowAll={() => replaceNavigate(createTodayTasksPath())}
        onAction={openCustomerAction}
        onSnooze={snoozeCustomerAction}
        onRetry={retryCustomerActions}
        onNavigate={navigate}
      />
    );
  }

  if (isMobileNotificationScreen) {
    return <MobileNotificationPage onBack={() => navigate("/tabs/home")} actionItems={visibleActionItems} />;
  }

  if (isReviewEventPhotoScreen && reviewEventStatus === REVIEW_EVENT_STATUS.ready) {
    return <ReviewEventPhotoDetailPage photos={reviewEventPhotos} onBack={closeHomeSubScreen} onNavigate={navigate} />;
  }

  if (isGalleryListScreen) {
    return (
      <GalleryPostListPage
        categoryId={searchParams.get("categoryId") || undefined}
        categoryName={searchParams.get("categoryName") || "스튜디오 갤러리"}
        onBack={closeHomeSubScreen}
        onNavigate={navigate}
        onPostClick={openReviewPhoto}
      />
    );
  }

  if (isMobileReviewPhotoScreen) {
    const selectedGalleryPost = getReviewPhotoSelection() || {
      id: searchParams.get("galleryPostId") || "",
      galleryPostId: searchParams.get("galleryPostId") || "",
      title: "스튜디오 갤러리",
    };
    return <ReviewPhotoDetailPage photo={selectedGalleryPost} onBack={closeHomeSubScreen} onNavigate={navigate} />;
  }

  if (isPublicContentScreen) {
    return (
      <PublicContentDetailPage
        item={selectedPublicContent}
        onBack={() => navigateBack("/tabs/home")}
        onNavigate={navigate}
      />
    );
  }

  if (isEventPromotionHubScreen) {
    return (
      <EventPromotionHubPage
        events={storeEvents}
        initialStatus={searchParams.get("status") || "all"}
        onBack={() => navigateBack("/tabs/home")}
        onEventClick={openStoreEventFromHub}
        onNavigate={navigate}
        onStatusChange={updateEventHubStatus}
      />
    );
  }

  if (isMobileStoreEventScreen) {
    return (
      <StoreEventHomePage
        event={selectedStoreEvent}
        isEventApplied={Boolean(selectedStoreEvent?.isApplied)}
        isApplying={eventApplyMutation.isPending || eventApplyMutation.isLoading}
        applyError={eventApplyError}
        onApply={applyStoreEvent}
        onBack={searchParams.get("from") === "events" ? () => navigateBack(getEventHubReturnPath()) : () => navigateBack("/tabs/home")}
        onViewEvents={returnToEventList}
        onNavigate={navigate}
      />
    );
  }

  const renderHomeSection = (section) => {
    const sectionProps = getSectionProps(section);

    if (section.type !== "native") {
      return <ConfigurableHomeSection key={section.key} section={section} onNavigate={navigate} />;
    }

    switch (section.key) {
      case "welcome":
        return <WelcomeCard key={section.key} userName={userName} {...sectionProps} />;
      case "today_tasks":
        return (
          <TodayTaskDashboard
            key={section.key}
            {...sectionProps}
            items={visibleActionItems}
            loading={areTodayTasksLoading}
            error={!areTodayTasksLoading && visibleActionItems.length === 0 && (effectiveActionDataStatus === "partial" || effectiveActionDataStatus === "error")}
            partial={!isShowingPreviewActions && visibleActionItems.length > 0 && (effectiveActionDataStatus === "partial" || effectiveActionDataStatus === "error")}
            onOpenAll={() => navigate(createTodayTasksPath())}
            onOpenFilter={(filterKey) => navigate(createTodayTasksPath({ filterKey }))}
            onOpenTask={(item) => navigate(createTodayTasksPath({ actionId: item.id }))}
            onRetry={retryCustomerActions}
          />
        );
      case "recent_shooting":
        return (
          <RecentShootingSection
            key={section.key}
            {...sectionProps}
            schedule={recentShooting}
            unavailable={homeAvailability.unavailable}
            loading={homeAvailability.loading}
            onViewAll={() => navigate("/tabs/schedules?category=completed")}
            onDownload={() => navigate(
              `/mypage/photos${recentShooting?.id ? `?scheduleId=${encodeURIComponent(recentShooting.id)}` : ""}`,
            )}
          />
        );
      case "upcoming_schedule":
        return (
          <UpcomingScheduleSection
            key={section.key}
            {...sectionProps}
            schedule={schedule}
            unavailable={homeAvailability.unavailable}
            loading={homeAvailability.loading}
            onScheduleClick={() => {
              if (
                schedule?.themeSelectionRequired
                && !schedule?.themeSelectionComplete
                && schedule?.id
              ) {
                const params = new URLSearchParams({
                  selectSchedule: schedule.id,
                  category: "upcoming",
                  requiredThemeCount: String(schedule.themeRequiredTotal || 1),
                });
                navigate(`/tabs/themes?${params.toString()}`);
                return;
              }
              navigate("/tabs/schedules?category=upcoming");
            }}
            onPrepClick={() => navigate("/tabs/schedules?category=upcoming")}
          />
        );
      case "review_event":
        return (
          <ReviewEventPhotoSection
            key={section.key}
            {...sectionProps}
            status={reviewEventStatus}
            photos={reviewEventPhotos}
            onClick={openReviewEventPhoto}
            onApply={() => navigate("/tabs/schedules")}
          />
        );
      case "events":
        return (
          <StoreEventSection
            key={section.key}
            {...sectionProps}
            events={storeEvents}
            myBranch={myBranch}
            onClick={openStoreEvent}
            onViewAll={openEventPromotionHub}
          />
        );
      case "review_gallery":
        return (
          <StudioReviewGallerySection
            key={section.key}
            {...sectionProps}
            title="스튜디오 갤러리"
            description={homeAvailability.preview ? "미리보기용 촬영 사진입니다. 고객님의 실제 사진이 아닙니다." : "실제 고객님들이 남겨주신 소중한 순간들을 만나보세요"}
            sections={gallerySections}
            onPhotoClick={openReviewPhoto}
            onViewAll={openGalleryList}
          />
        );
      case "popular_themes":
        return (
          <PopularThemeSection
            key={section.key}
            {...sectionProps}
            themes={popularThemes}
            onThemeClick={(theme) => navigate(
              theme?.id
                ? `/tabs/themes?themeId=${encodeURIComponent(theme.id)}`
                : "/tabs/themes",
            )}
          />
        );
      case "notice":
        return (
          <NoticeSection
            key={section.key}
            {...sectionProps}
            noticeItems={noticeCenterItems}
            onNavigate={navigate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <section className="figma-home-page mobile-app-frame" data-node-id="6368:9697">
      <StatusBar />
      <MobileTopHeader
        onBellClick={() => navigate(withMobileView("/tabs/home?screen=notifications"))}
        notificationCount={visibleActionItems.length}
      />
      <main className="figma-home-scroll">
        <ConfiguredPageIntro pageConfig={homePageConfig} />
        {homeAvailability.showNotice && (
          <aside className="figma-home-connection" aria-label="홈 연결 상태" role="status">
            <div>
              <strong>{isPublicDemoSession ? "마이스튜디오 체험 모드입니다" : homeAvailability.accessDenied ? "계정의 로그인 상태와 접근 권한을 확인해 주세요" : homeAvailability.loading ? "스튜디오 정보를 연결하고 있어요" : homeAvailability.liveHome ? "최신 정보를 불러오지 못했어요" : "스튜디오 연결을 기다리고 있어요"}</strong>
              <p>{isPublicDemoSession
                ? "예시 사진과 공지를 둘러보세요. 실제 고객 정보는 조회·변경되지 않습니다."
                : homeAvailability.preview
                ? "사진과 공지는 미리보기입니다. 실제 예약·개인 사진은 연결 후 확인할 수 있어요."
                : homeAvailability.liveHome
                  ? "마지막으로 불러온 정보를 표시하고 있어요. 다시 연결해 최신 정보를 확인해 주세요."
                  : "기본 메뉴는 이용할 수 있어요. 고객님의 정보는 연결 후 표시됩니다."}</p>
            </div>
            {isPublicDemoSession ? null : homeQuery.error?.kind === "authentication" ? (
              <a href="/?screen=login">로그인</a>
            ) : homeQuery.configured ? (
              <button type="button" disabled={homeQuery.isFetching || homeAvailability.loading} onClick={retryCustomerActions}>
                {homeQuery.isFetching || homeAvailability.loading ? "연결 중…" : "다시 연결"}
              </button>
            ) : null}
          </aside>
        )}
        <div className="figma-home-event-gallery-group figma-home-config-sections">
          {visibleHomeSections.map(renderHomeSection)}
        </div>
      </main>
      <MobileBottomTabBar activePath={window.location.pathname} onNavigate={navigate} />
    </section>
  );
}

export {
  ErpImage,
  NoticeCenterPanel,
  StoreEventSection,
  StudioReviewGallerySection,
};

